import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  Skeleton,
  LinearProgress,
  Paper,
  Avatar,
  Stack,
  Container,
  CardActions,
  Zoom,
  Slide,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Switch,
  FormControlLabel,
  Autocomplete,
  Pagination,
  Tooltip
} from '@mui/material';
import {
  Image as ImageIcon,
  AutoAwesome as AutoAwesomeIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Facebook as FacebookIcon,
  Google as GoogleIcon,
  Language as LanguageIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
  CloudUpload as CloudUploadIcon,
  Collections as CollectionsIcon,
  SelectAll as SelectAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  DirectionsCar as DirectionsCarIcon,
  Block as BlockIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';

// Helper to render active/inactive status chip
const StatusChip = ({ active, onToggle, loading }) => {
  const { t } = useTranslation();
  const isActive = active;
  const iconColor = isActive ? '#27ae60' : '#e74c3c';

  const handleClick = (event) => {
    event.stopPropagation();
    if (!loading && onToggle) {
      onToggle();
    }
  };

  return (
    <Chip
      label={isActive ? t('aiGenerator.active') : t('aiGenerator.inactive')}
      size="small"
      variant="outlined"
      clickable={Boolean(onToggle)}
      onClick={handleClick}
      disabled={loading}
      icon={
        loading ? (
          <CircularProgress size={14} sx={{ color: iconColor }} />
        ) : (
          isActive ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <ErrorIcon sx={{ fontSize: 16 }} />
        )
      }
      sx={{
        pr: { xs: 0.75, sm: 1 },
        height: 24,
        borderRadius: 2,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: isActive ? 'rgba(39, 174, 96, 0.08)' : 'rgba(231, 76, 60, 0.08)',
        color: isActive ? '#1e7e34' : '#c0392b',
        borderColor: isActive ? 'rgba(39, 174, 96, 0.35)' : 'rgba(231, 76, 60, 0.35)',
        flexShrink: 0,
        cursor: onToggle && !loading ? 'pointer' : 'default',
        opacity: loading ? 0.75 : 1,
        transition: 'all 0.2s ease',
        '&:hover': onToggle && !loading ? {
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(39, 174, 96, 0.15)'
        } : undefined,
        '& .MuiChip-label': {
          px: { xs: 0.75, sm: 1 },
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          whiteSpace: 'nowrap'
        },
        '& .MuiChip-icon': {
          color: iconColor,
          ml: 0.5
        }
      }}
    />
  );
};

// Memoized Image Card Component for better performance
const XHSImageCard = React.memo(({ image, isSelected, onSelect }) => {
  return (
    <Box
      sx={{
        width: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)', lg: 'calc(25% - 12px)' },
        minWidth: 0,
        flexShrink: 0
      }}
    >
      <Card
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease', // Faster transition
          border: isSelected ? '3px solid #ff6b6b' : '2px solid transparent',
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)'
          }
        }}
        onClick={() => onSelect(image)}
      >
        {/* Selection indicator */}
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: '#ff6b6b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              border: '2px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
          </Box>
        )}
        <Box
          component="img"
          src={image.image_url}
          alt={`XHS Image ${image.id}`}
          loading="lazy"
          decoding="async"
          sx={{
            width: '100%',
            height: { xs: 250, sm: 250, md: 200, lg: 180 },
            objectFit: 'contain',
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            willChange: 'transform' // Optimize for hover effect
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <Box
          sx={{
            width: '100%',
            height: { xs: 250, sm: 250, md: 200, lg: 180 },
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: 1,
            flexDirection: 'column'
          }}
        >
          <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
        </Box>
      </Card>
    </Box>
  );
}, (prevProps, nextProps) => {
  return prevProps.isSelected === nextProps.isSelected && prevProps.image.id === nextProps.image.id;
});

const XHSContentGenerator = ({ canManageVehicleModels = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editDetailsData, setEditDetailsData] = useState({ title: '', content: '', hashtags: '', category: '', is_active: true });
  const [savingDetails, setSavingDetails] = useState(false);

  // Platform content states
  const [platformContent, setPlatformContent] = useState([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformTotalCount, setPlatformTotalCount] = useState(0);
  const [contentPage, setContentPage] = useState(1);
  const [contentItemsPerPage] = useState(24); // Items per page for content listing

  // AI Generation states
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const selectedPlatform = 'xhs'; // Hardcoded for XHS
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    location: '',
    sellingPoint: '',
    businessNature: '',
    hashtags: '',
    language: 'Chinese',
    category: '',
  });

  // Image upload states
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]); // Multiple images
  // eslint-disable-next-line no-unused-vars
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]); // Multiple image previews
  const [uploadingImage, setUploadingImage] = useState(false);
  const [batchCount, setBatchCount] = useState(1); // Number of records to generate
  const [imageSelectorPurpose, setImageSelectorPurpose] = useState('generator'); // 'generator' | 'edit'
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [editSelectedLibraryImage, setEditSelectedLibraryImage] = useState(null);

  // Batch generation progress states
  const [batchProgress, setBatchProgress] = useState({
    isBatchGenerating: false,
    current: 0,
    total: 0,
    completed: 0,
    failed: 0
  });

  // XHS Images states
  const [xhsImages, setXhsImages] = useState([]);
  const [selectedXhsImage, setSelectedXhsImage] = useState(null);
  const [selectedXhsImages, setSelectedXhsImages] = useState([]); // Multiple XHS images
  const [loadingXhsImages, setLoadingXhsImages] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [copied, setCopied] = useState({ title: false, content: false, hashtags: false });
  const [editDialog, setEditDialog] = useState({ open: false, id: null, content: '', title: '', hashtags: '', image_url: '', category: '', is_active: true });
  const [contentFilter, setContentFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [categoryFilter, setCategoryFilter] = useState(''); // '' means all categories
  const [activatingAll, setActivatingAll] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [quota, setQuota] = useState({ quota_limit: 100, used_quota: 0 });
  const [categories, setCategories] = useState([]);

  // Input history states
  const [inputHistory, setInputHistory] = useState([]);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const historyMenuOpen = Boolean(historyAnchorEl);

  // XHS image selector pagination
  const [xhsImagePage, setXhsImagePage] = useState(1);
  const [xhsImageTotalPages, setXhsImageTotalPages] = useState(1);
  const [xhsItemsPerPage, setXhsItemsPerPage] = useState(50);

  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const editPreviewSource = editImagePreview || editDialog.image_url || '';

  // Load saved inputs from database
  useEffect(() => {
    const loadSavedInputs = async () => {
      try {
        const brand = getCurrentBrand();
        const response = await fetch(`${API_ENDPOINTS.INPUT_HISTORY}?brand=${brand}&platform=xhs`);

        if (response.ok) {
          const historyList = await response.json();
          if (Array.isArray(historyList) && historyList.length > 0) {
            setInputHistory(historyList);
            if (historyList[0] && historyList[0].content) {
              // Create a copy of content but exclude hashtags to prevent old hashtags from persisting
              // eslint-disable-next-line no-unused-vars
              const { hashtags, ...contentWithoutHashtags } = historyList[0].content;

              setBusinessInfo(prev => ({
                ...prev,
                ...contentWithoutHashtags,
                hashtags: '' // Explicitly reset hashtags
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading saved inputs:', error);
      }
    };

    loadSavedInputs();
  }, []);

  // Note: History is now saved when user clicks Generate button (see handleGenerateContent)

  // Load history entry into form
  // Load history entry into form
  const loadHistoryEntry = (entry) => {
    if (entry && entry.content) {
      setBusinessInfo(entry.content);
      setHistoryAnchorEl(null);
    }
  };

  // Delete history entry
  const deleteHistoryEntry = async (id, event) => {
    event.stopPropagation();
    try {
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.INPUT_HISTORY}/${id}?brand=${brand}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const updatedHistory = inputHistory.filter(entry => entry.id !== id);
        setInputHistory(updatedHistory);
      } else {
        console.error('Failed to delete history entry');
      }
    } catch (error) {
      console.error('Error deleting history entry:', error);
    }
  };

  // Fetch XHS images from API
  const fetchImages = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString()
      });

      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      // Response is fetched but data is not used in this component
      await response.json();
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Fetch XHS images for selection
  const fetchXhsImages = useCallback(async (page = 1) => {
    try {
      setLoadingXhsImages(true);
      setError(null);

      const offset = (page - 1) * xhsItemsPerPage;
      const imageParams = new URLSearchParams({
        limit: xhsItemsPerPage.toString(),
        offset: offset.toString(),
        brand: getCurrentBrand()
      });

      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?${imageParams}`);

      if (!response.ok) {
        throw new Error('Failed to fetch XHS images');
      }

      const data = await response.json();
      setXhsImages(data.images || []);
      setXhsImagePage(page);

      // Calculate total pages
      const totalPages = Math.ceil((data.totalCount || 0) / xhsItemsPerPage);
      setXhsImageTotalPages(totalPages);
    } catch (err) {
      console.error('Error fetching XHS images:', err);
      setError(err.message);
    } finally {
      setLoadingXhsImages(false);
    }
  }, [xhsItemsPerPage]);

  // Fetch platform content from API
  const fetchPlatformContent = useCallback(async (platform) => {
    try {
      setPlatformLoading(true);
      setError(null);

      let endpoint;
      switch (platform) {
        case 'google':
          endpoint = API_ENDPOINTS.GOOGLE_CONTENT;
          break;
        case 'facebook':
          endpoint = API_ENDPOINTS.FACEBOOK_CONTENT;
          break;
        case 'xhs':
          endpoint = API_ENDPOINTS.XHS_CONTENT;
          break;
        default:
          return;
      }

      const queryParams = new URLSearchParams({
        limit: '100', // Reasonable limit for initial load, pagination will handle more
        offset: '0',
        includeInactive: 'true', // Always fetch all records to support filtering
        brand: getCurrentBrand() // Add brand parameter
      });

      const response = await fetch(`${endpoint}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${platform} content`);
      }

      const data = await response.json();

      // Handle different response structures
      if (platform === 'google') {
        setPlatformContent(data.reviews || []);
      } else if (platform === 'facebook') {
        setPlatformContent(data.posts || []);
      } else if (platform === 'xhs') {
        setPlatformContent(data.posts || []);
      }

      // Update total count for all platforms
      setPlatformTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error(`Error fetching ${platform} content:`, err);
      setError(err.message);
    } finally {
      setPlatformLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchImages(currentPage);
  }, [currentPage, fetchImages]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.CATEGORIES}?brand=${brand}&platform=xhs`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Load platform content when component mounts
  useEffect(() => {
    fetchPlatformContent(selectedPlatform);
    fetchCategories();
    setBusinessInfo(prev => ({ ...prev, language: 'Chinese' }));
  }, [fetchPlatformContent, fetchCategories]);

  // Always enforce Chinese language for XHS platform to match generated content
  useEffect(() => {
    if (selectedPlatform === 'xhs' && businessInfo.language !== 'Chinese') {
      setBusinessInfo(prev => ({ ...prev, language: 'Chinese' }));
    }
  }, [selectedPlatform, businessInfo.language]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setContentPage(1);
  }, [contentFilter, categoryFilter]);

  // Fetch quota
  const fetchQuota = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.QUOTA_BY_PLATFORM('xhs')}?brand=${getCurrentBrand()}`);
      if (response.ok) {
        const quotaData = await response.json();
        setQuota(quotaData);
      }
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  useEffect(() => {
    if (editDialog.open && selectedPlatform === 'xhs') {
      setEditImagePreview(editDialog.image_url || '');
    }
    if (!editDialog.open) {
      setEditImageFile(null);
      setEditSelectedLibraryImage(null);
    }
  }, [editDialog.open, editDialog.image_url, selectedPlatform]);


  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setIsEditingDetails(false);
    setEditDetailsData({ title: '', content: '', hashtags: '', category: '', is_active: true });
  };

  const handleCloseEditDialog = () => {
    setEditDialog(prev => ({ ...prev, open: false }));
    setEditImageFile(null);
    setEditImagePreview('');
    setEditSelectedLibraryImage(null);
  };

  const handleEditDetails = () => {
    if (selectedImage) {
      setEditDetailsData({
        title: selectedImage.title || '',
        content: selectedImage.content || '',
        hashtags: selectedImage.hashtags || '',
        category: selectedImage.category || '',
        is_active: selectedImage.is_active !== false
      });
      setIsEditingDetails(true);
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedImage) return;

    setSavingDetails(true);
    try {
      const brand = getCurrentBrand();
      const endpoint = `${API_ENDPOINTS.XHS_CONTENT}/${selectedImage.id}?brand=${brand}`;
      const payload = {
        title: editDetailsData.title,
        content: editDetailsData.content,
        hashtags: editDetailsData.hashtags,
        category: editDetailsData.category,
        image_url: selectedImage.image_url || '',
        is_active: editDetailsData.is_active
      };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update content');
      }

      // Update the selectedImage state with new data
      setSelectedImage({
        ...selectedImage,
        title: editDetailsData.title,
        content: editDetailsData.content,
        hashtags: editDetailsData.hashtags,
        category: editDetailsData.category,
        is_active: editDetailsData.is_active,
        updated_at: new Date().toISOString()
      });

      // Update the content in the list
      setPlatformContent(prevContent =>
        prevContent.map(item =>
          item.id === selectedImage.id
            ? {
              ...item,
              title: editDetailsData.title,
              content: editDetailsData.content,
              hashtags: editDetailsData.hashtags,
              category: editDetailsData.category,
              is_active: editDetailsData.is_active,
              updated_at: new Date().toISOString()
            }
            : item
        )
      );

      setIsEditingDetails(false);
      // Refresh the content list to get updated data
      await fetchPlatformContent('xhs');
    } catch (error) {
      console.error('Error saving details:', error);
      alert(t('aiGenerator.errorSavingChanges') || 'Error saving changes. Please try again.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDetails(false);
    setEditDetailsData({ title: '', content: '', hashtags: '', category: '', is_active: true });
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAllOnPage = () => {
    const newSelected = new Set(selectedIds);
    platformContent.forEach(item => {
      // Apply current filters if any
      let shouldSelect = true;
      if (contentFilter === 'active' && item.is_active === false) shouldSelect = false;
      if (contentFilter === 'inactive' && item.is_active !== false) shouldSelect = false;
      if (categoryFilter && (!item.category || item.category !== categoryFilter)) shouldSelect = false;

      if (shouldSelect) {
        newSelected.add(item.id);
      }
    });
    setSelectedIds(newSelected);
  };

  const selectAllOnCurrentPage = () => {
    const newSelected = new Set(selectedIds);

    // Filter first
    const filteredContent = platformContent.filter(item => {
      if (contentFilter === 'active' && item.is_active === false) return false;
      if (contentFilter === 'inactive' && item.is_active !== false) return false;
      if (categoryFilter && (!item.category || item.category !== categoryFilter)) return false;
      return true;
    });

    // Then paginate
    const startIndex = (contentPage - 1) * contentItemsPerPage;
    const endIndex = startIndex + contentItemsPerPage;
    const paginatedItems = filteredContent.slice(startIndex, endIndex);

    paginatedItems.forEach(item => {
      newSelected.add(item.id);
    });

    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;

    setBulkLoading(true);
    try {
      const updates = Array.from(selectedIds).map(id => {
        const item = platformContent.find(i => i.id === id);
        if (item && item.is_active !== false) {
          return fetch(`${API_ENDPOINTS.XHS_CONTENT}/${id}?brand=${getCurrentBrand()}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...item,
              is_active: false
            }),
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
      await fetchPlatformContent(selectedPlatform);
      clearSelection();
      // Show success message
      const event = new CustomEvent('show-alert', {
        detail: { message: t('aiGenerator.bulkDeactivateSuccess') || 'Selected items deactivated successfully', severity: 'success' }
      });
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error deactivating items:', err);
      setError(t('aiGenerator.bulkActionError') || 'Failed to perform bulk action');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedIds.size === 0) return;

    setBulkLoading(true);
    try {
      const updates = Array.from(selectedIds).map(id => {
        const item = platformContent.find(i => i.id === id);
        if (item && item.is_active === false) {
          return fetch(`${API_ENDPOINTS.XHS_CONTENT}/${id}?brand=${getCurrentBrand()}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...item,
              is_active: true
            }),
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
      await fetchPlatformContent(selectedPlatform);
      clearSelection();
      // Show success message
      const event = new CustomEvent('show-alert', {
        detail: { message: t('aiGenerator.bulkActivateSuccess') || 'Selected items activated successfully', severity: 'success' }
      });
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error activating items:', err);
      setError(t('aiGenerator.bulkActionError') || 'Failed to perform bulk action');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    if (!window.confirm(t('aiGenerator.confirmBulkDelete') || 'Are you sure you want to delete the selected items?')) {
      return;
    }

    setBulkLoading(true);
    try {
      const deletions = Array.from(selectedIds).map(id =>
        fetch(`${API_ENDPOINTS.XHS_CONTENT}/${id}?brand=${getCurrentBrand()}`, {
          method: 'DELETE',
        })
      );

      await Promise.all(deletions);
      await fetchPlatformContent(selectedPlatform);
      clearSelection();
      // Show success message
      const event = new CustomEvent('show-alert', {
        detail: { message: t('aiGenerator.bulkDeleteSuccess') || 'Selected items deleted successfully', severity: 'success' }
      });
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error deleting items:', err);
      setError(t('aiGenerator.bulkActionError') || 'Failed to perform bulk action');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleToggleStatus = useCallback(async (item) => {
    if (!item?.id) {
      return;
    }

    const currentActive = item.is_active !== false;
    const nextStatus = !currentActive;

    const confirmationMessage = currentActive
      ? t('aiGenerator.confirmDeactivate') || 'Deactivate this record?'
      : t('aiGenerator.confirmActivate') || 'Activate this record?';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    setStatusUpdatingId(item.id);
    setError(null);

    const applyStatusToState = (status) => {
      setPlatformContent(prevContent =>
        prevContent.map(content =>
          content.id === item.id ? { ...content, is_active: status } : content
        )
      );
      setEditDialog(prev => (prev && prev.id === item.id ? { ...prev, is_active: status } : prev));
      setSelectedImage(prev => (prev && prev.id === item.id ? { ...prev, is_active: status } : prev));
    };

    applyStatusToState(nextStatus);

    try {
      const brand = getCurrentBrand();
      const endpoint = `${API_ENDPOINTS.XHS_CONTENT}/${item.id}?brand=${brand}`;
      const payload = {
        title: item.title || '',
        content: item.content || '',
        hashtags: item.hashtags || '',
        image_url: item.image_url || '',
        category: item.category || '',
        is_active: nextStatus
      };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      applyStatusToState(currentActive);
      setError(err.message || 'Failed to update status');
    } finally {
      setStatusUpdatingId(null);
    }
  }, [t]);

  // Handle delete content
  const handleDeleteContent = async (id) => {
    if (!window.confirm(t('aiGenerator.deleteContentConfirm') || 'Are you sure you want to delete this content? This action cannot be undone.')) {
      return;
    }

    try {
      const brand = getCurrentBrand();
      const endpoint = `${API_ENDPOINTS.XHS_CONTENT}/${id}?brand=${brand}`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to delete content');
      }

      // Refresh the content list
      await fetchPlatformContent(selectedPlatform);
      setError(null);
    } catch (err) {
      console.error('Error deleting content:', err);
      setError(t('aiGenerator.errorDeletingContent') || 'Error deleting content. Please try again.');
    }
  };

  // Handle activate all inactive content
  const handleActivateAllInactive = async () => {
    const inactiveItems = platformContent.filter(item => item.is_active === false);
    if (inactiveItems.length === 0) {
      return;
    }

    if (!window.confirm(t('aiGenerator.activateAllInactiveConfirm') || `Are you sure you want to activate ${inactiveItems.length} inactive content item(s)?`)) {
      return;
    }

    setActivatingAll(true);
    setError(null);

    try {
      const endpoint = API_ENDPOINTS.XHS_CONTENT;
      let successCount = 0;
      let failCount = 0;

      // Update each inactive item
      const brand = getCurrentBrand();
      for (const item of inactiveItems) {
        try {
          const updateEndpoint = `${endpoint}/${item.id}?brand=${brand}`;
          const payload = {
            title: item.title || '',
            content: item.content || '',
            hashtags: item.hashtags || '',
            image_url: item.image_url || '',
            is_active: true
          };

          const response = await fetch(updateEndpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error(`Error activating content ${item.id}:`, err);
          failCount++;
        }
      }

      // Refresh the content list
      await fetchPlatformContent(selectedPlatform);

      if (failCount > 0) {
        setError(`${successCount} content item(s) activated successfully, ${failCount} failed.`);
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Error activating all inactive content:', err);
      setError(t('aiGenerator.errorActivatingAll') || 'Error activating content. Please try again.');
    } finally {
      setActivatingAll(false);
    }
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedImage(null);
  };


  // Generate platform-specific prompt
  const generatePrompt = (platform, businessInfo, imageUrl = null) => {
    const { name, location, sellingPoint, businessNature, hashtags, language } = businessInfo;
    switch (platform) {
      case 'google':
        return `
You are simulating a *completely different real customer every time* writing a Google Maps review for "${name}" in ${language}. Each review must have a UNIQUE opening phrase that sounds like it's written by a different person with their own voice, experience, and perspective.

Business Info:
- Name: ${name}
- Location: ${location}
- Category: ${businessNature}
- Highlight: ${sellingPoint}

IMPORTANT:
- Even if writing in Chinese, keep the location name in its ORIGINAL English/Romanized form (e.g., "Bukit Jalil", "SS2", "PJ"). DO NOT translate the location name.

🇲🇾 Malaysian Style Opening Generation:
- Generate COMPLETELY UNIQUE opening phrases every time (never repeat the same opening)
- Create 500+ different opening variations naturally through AI creativity
- Each opening should feel like a different real customer's voice
- Vary between personal experiences, recommendations, discoveries, and stories
- Use different emotional tones: excited, satisfied, surprised, grateful, impressed
- Include diverse starting scenarios: first-time visits, repeat customers, referrals, urgent needs, casual discoveries
- Mix different time references: recent visits, regular patronage, special occasions, seasonal visits
- Vary sentence structures: questions, statements, exclamations, storytelling
- Create authentic Malaysian English expressions and local context

🎯 Writing Style:
1. Use casual Malaysian English (or Malaysian Chinese style if language is Chinese)
2. Sound like a real local sharing experience
3. Include authentic details about the ${businessNature}
4. Reference specific services or products
5. Mention real benefits or solutions
6. Keep it natural and conversational
7. Add genuine emotional reaction
8. Integrate ${sellingPoint} naturally

✨ Dynamic Elements:
- Adjust tone based on business type
- Include industry-specific details
- Use relevant terminology for ${businessNature}
- Reference common customer needs
- Mention typical service scenarios
- Include practical benefits
- Add credibility markers

🚫 AVOID:
- Generic business descriptions
- Overly formal language
- Marketing-style writing
- Technical jargon
- Repetitive patterns
- Standard opening phrases
- Using the same opening structure repeatedly

✨ CRITICAL REQUIREMENT:
- Generate a COMPLETELY UNIQUE opening phrase that sounds like a different real customer every single time
- Never use the same opening pattern or structure
- Let AI creativity generate 500+ different opening variations naturally
- Each review should feel like it's written by a completely different person
- Location names MUST remain in English/Romanized form

Create ONE genuine Malaysian-style review (50-80 words) that naturally fits the ${businessNature} category and sounds like a real customer sharing their experience with a completely unique opening phrase.`;

      case 'facebook':
        return `Write a casual Facebook post about "${name}" business like a real person sharing their experience with friends.
        
Business Details:
- Business Name: ${name}
- Location: ${location}
- Business Nature: ${businessNature}
- Selling Point: ${sellingPoint}
- Language: ${language}

Requirements:
- Write like a real person talking to friends on Facebook
- Use natural, conversational language in ${language}
- Keep it casual and personal (40-80 words max)
- Sound authentic and genuine
- No hashtags needed, no marketing language
- Write as if you're sharing a personal experience
- Use everyday language people actually speak
- Start with completely unique, AI-generated opening phrases (never repeat the same opening)
- Be conversational and relatable
- If there's a selling point, mention it naturally in context
- Focus on the personal experience and what you enjoyed
- Include specific details about what made it special
- End with enthusiasm or recommendation
- Make it sound like a genuine social media post
- CRITICAL: Location names (e.g., "Bukit Jalil", "SS2", "PJ") must remain in their ORIGINAL English/Romanized form - NEVER translate location names to Chinese characters

Write a natural, personal Facebook post that sounds like a real person sharing their experience.`;

      case 'xhs':
      default:
        const imageAnalysis = imageUrl ? `
- Image Analysis: Analyze the uploaded image and incorporate visual elements into the content
- Describe what you see in the image based on ACTUAL visual evidence
- Do NOT hallucinate or make up details that are not visible in the image
- Use the image to enhance the authenticity of the post` : '';
        const openingDirections = [
          'start with a specific visual detail from the image',
          'start with a location-based observation',
          'start with a personal moment before visiting',
          'start with a small surprise from the experience',
          'start with a practical reason someone would choose this business',
          'start with the atmosphere or service feeling',
          'start with a direct recommendation to a friend',
          'start with a specific product/service detail'
        ];
        const openingDirection = openingDirections[Math.floor(Math.random() * openingDirections.length)];
        const titleDirections = [
          'use a visual-detail hook',
          'use a location-discovery hook',
          'use a benefit-focused hook',
          'use a personal-experience hook',
          'use an atmosphere/service hook',
          'use a practical recommendation hook',
          'use a curiosity-style hook',
          'use a product/service-specific hook'
        ];
        const titleDirection = titleDirections[Math.floor(Math.random() * titleDirections.length)];

        return `Generate a natural, authentic Xiaohongshu (小红书) post about "${name}" business in Chinese with Malaysian style and specific details.
        
Business Details:
- Business Name: ${name}
- Location: ${location}
- Selling Point: ${sellingPoint}
- Business Nature: ${businessNature}
- Referenced Hashtags: ${hashtags}
- Language: ${language}${imageAnalysis}

Requirements:
- Write ONLY in Chinese (简体中文) with Malaysian Chinese style
- IMPORTANT: Keep location names in their ORIGINAL form (e.g., "Kuchai Lama", "Bukit Jalil", "SS2", "PJ" should remain as-is, NEVER translate to Chinese)
- CRITICAL: Strictly adhere to the Business Nature: "${businessNature}"
- Do NOT hallucinate features that don't match the Business Nature.
- Generate content in this EXACT JSON format:
{
  "title": "吸引人的标题",
  "content": "详细的正文内容（描述体验、感受等）",
  "hashtags": "#标签1 #标签2 #标签3 #标签4"
}

Title requirements:
- 25-35 characters max
- Create catchy titles with Malaysian Chinese style
- Use real like and unique title patterns
- Be creative and unique - avoid repetitive patterns
- Include relevant emojis that match the business nature and mood
- Title direction for THIS generation: ${titleDirection}
- Avoid using the same title structure as previous posts

Content requirements (CRITICAL - make this specific and engaging with real details):
- 250-350 Chinese characters (concise but detailed content)
- Write like a real Malaysian person sharing a genuine experience
- Use Malaysian Chinese style and local references
- Opening direction for THIS generation: ${openingDirection}
- Start with a completely unique first sentence based on the opening direction above
- Describe the experience with SPECIFIC details based on the Business Nature:
- Use Malaysian Chinese expressions and style
- Include local references and Malaysian context
- End with strong recommendation and personal endorsement
- Write like you're telling a Malaysian friend about your experience
- Make it feel personal, authentic, and conversational with local flavor
- Use varied sentence structures and natural transitions
- DO NOT include hashtags in the content - hashtags will be in separate field
- CRITICAL: Location names (e.g., "Kuchai Lama", "Bukit Jalil", "SS2", "PJ", "KL", etc.) must remain in their ORIGINAL English/Romanized form - NEVER translate location names to Chinese characters (e.g. DO NOT use "古仔大街", use "Kuchai Lama")

Hashtags requirements:
${hashtags ? `- MANDATORY HASHTAGS (MUST include ALL of these in EVERY post): ${hashtags.split(/[,\s]+/).filter(h => h.trim()).map(h => h.startsWith('#') ? h : '#' + h).join(' ')}` : ''}
- CRITICAL: You MUST include EVERY SINGLE hashtag from the mandatory list above in your response
- DO NOT skip, modify, or replace any of the mandatory hashtags
- If fewer than 5 mandatory hashtags are provided, add 1-2 additional relevant Malaysian-style hashtags to reach 4-6 total
- If 5 or more mandatory hashtags are provided, use ONLY those hashtags
- Include the business name as a hashtag ONLY if it's not already in the mandatory hashtags (e.g., if business name is "${name}" and hashtags don't already contain it, add #${name.replace(/\s+/g, '')})
- DO NOT create duplicate hashtags
- Example: If mandatory hashtags are "#louwong #bestchickenrice #ss18", your hashtags field MUST contain all three: "#louwong #bestchickenrice #ss18 #美食推荐 #打卡必去"

Generate ONLY the JSON response, no other text.`;
    }
  };

  // Handle AI Content Generation
  const handleGenerateContent = async () => {
    try {
      setGenerating(true);
      setError(null);

      // Save to history before generating
      try {
        if (businessInfo.name || businessInfo.location) {
          // Check if content is different from last entry
          const shouldSave = inputHistory.length === 0 ||
            JSON.stringify(inputHistory[0]?.content) !== JSON.stringify(businessInfo);

          if (shouldSave) {
            const brand = getCurrentBrand();
            const response = await fetch(API_ENDPOINTS.INPUT_HISTORY, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                brand,
                platform: 'xhs',
                content: businessInfo
              })
            });

            if (response.ok) {
              const newEntry = await response.json();
              setInputHistory(prev => [newEntry, ...prev].slice(0, 5));
            }
          }
        }
      } catch (historyError) {
        console.error('Error saving to history:', historyError);
        // Continue with generation even if history save fails
      }

      // Validate required fields
      if (!businessInfo.name.trim()) {
        setError('Business name is required');
        setGenerating(false);
        return;
      }
      if (!businessInfo.location.trim()) {
        setError('Location is required');
        setGenerating(false);
        return;
      }
      if (!businessInfo.businessNature.trim()) {
        setError('Business nature is required');
        setGenerating(false);
        return;
      }
      // Only require hashtags for XHS platform
      if (selectedPlatform === 'xhs' && !businessInfo.hashtags.trim()) {
        setError('Hashtags are required for Xiaohongshu content');
        setGenerating(false);
        return;
      }

      // Validate batch count
      const count = parseInt(batchCount) || 1;
      if (count < 1) {
        setError('Number of records must be at least 1');
        setGenerating(false);
        return;
      }

      // Check if adding new records would exceed the 1500 limit
      const MAX_LIMIT = 1500;
      if (platformTotalCount + count > MAX_LIMIT) {
        const remaining = MAX_LIMIT - platformTotalCount;
        if (remaining <= 0) {
          setError(`Content limit reached! You have reached the maximum of ${MAX_LIMIT.toLocaleString()} records for this platform. Please delete some content before creating new ones.`);
        } else {
          setError(`Content limit exceeded! You can only create ${remaining} more record${remaining === 1 ? '' : 's'}. Current: ${platformTotalCount.toLocaleString()}/${MAX_LIMIT.toLocaleString()}`);
        }
        setGenerating(false);
        return;
      }

      // Handle batch generation. If count > 1 on XHS, we batch regardless of images.
      const shouldBatch = count > 1 && selectedPlatform === 'xhs';

      if (shouldBatch) {
        // Batch generation: multiple records with random images
        let availableImageUrls = [];

        // Upload multiple images if files are uploaded
        if (uploadedImages && uploadedImages.length > 0) {
          const uploadedUrls = await handleMultipleImageUpload();
          if (uploadedUrls.length === 0) {
            setError(t('aiGenerator.failedToUploadImages'));
            setGenerating(false);
            return;
          }
          availableImageUrls = uploadedUrls;
        } else if (selectedXhsImages && selectedXhsImages.length > 0) {
          // Use selected XHS images
          availableImageUrls = selectedXhsImages.map(img => img.image_url);
        }

        // If no images are available, we'll still generate text-only posts.

        // Filter businessInfo
        const filteredBusinessInfo = {
          name: businessInfo.name,
          location: businessInfo.location,
          businessNature: businessInfo.businessNature,
          sellingPoint: businessInfo.sellingPoint,
          hashtags: businessInfo.hashtags,
          language: businessInfo.language,
          category: businessInfo.category
        };

        // Generate multiple records with progress tracking
        setBatchProgress({
          isBatchGenerating: true,
          current: 0,
          total: count,
          completed: 0,
          failed: 0
        });

        const results = [];
        const BATCH_SIZE = 3;

        for (let i = 0; i < count; i += BATCH_SIZE) {
          const batchPromises = [];
          const end = Math.min(i + BATCH_SIZE, count);

          for (let j = i; j < end; j++) {
            // Cycle through images to ensure all are used
            const randomImageUrl = availableImageUrls.length > 0
              ? availableImageUrls[j % availableImageUrls.length]
              : null;
            const prompt = generatePrompt(selectedPlatform, businessInfo, randomImageUrl);

            const requestBody = {
              businessInfo: filteredBusinessInfo,
              platform: selectedPlatform,
              prompt: prompt,
              imageUrl: randomImageUrl
            };

            batchPromises.push(
              fetch(`${API_ENDPOINTS.GENERATE_CONTENT}?brand=${getCurrentBrand()}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
              })
                .then(response => {
                  if (!response.ok) {
                    throw new Error(t('aiGenerator.failedToGenerateContent', { index: j + 1 }));
                  }
                  setBatchProgress(prev => ({
                    ...prev,
                    current: prev.current + 1,
                    completed: prev.completed + 1
                  }));
                  return response.json();
                })
                .catch(error => {
                  setBatchProgress(prev => ({
                    ...prev,
                    current: prev.current + 1,
                    failed: prev.failed + 1
                  }));
                  console.error(`Generation failed for item ${j + 1}:`, error);
                  return null;
                })
            );
          }

          // Wait for the current batch to complete
          const batchResults = await Promise.all(batchPromises);

          // Filter out failed requests (nulls) and add to results
          const successfulBatchResults = batchResults.filter(r => r !== null);
          results.push(...successfulBatchResults);

          // Add a small delay between batches to prevent rate limiting
          if (end < count) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        // Set the last generated content as the displayed result
        setGeneratedContent(results[results.length - 1]);

        // Clear only uploaded images after successful batch generation, but keep selected XHS images
        // This allows users to regenerate with the same selected images
        setUploadedImage(null);
        setUploadedImages([]);
        setImagePreview(null);
        setImagePreviews([]);
        // Keep selectedXhsImages and selectedXhsImage so they can be reused

        // Refresh platform content
        fetchPlatformContent(selectedPlatform);
        await fetchQuota();

        // Reset batch progress
        setBatchProgress({
          isBatchGenerating: false,
          current: 0,
          total: 0,
          completed: 0,
          failed: 0
        });

        // Show success message
        console.log(`✅ Successfully generated ${results.length} records`);

      } else {
        // Single generation (original behavior)
        let imageUrl = null;
        if (selectedPlatform === 'xhs') {
          if (uploadedImage) {
            // Handle single file upload (backward compatibility)
            imageUrl = await handleImageUpload();
            if (!imageUrl) {
              setGenerating(false);
              return;
            }
          } else if (uploadedImages && uploadedImages.length > 0) {
            // Handle single or multiple files from uploadedImages array
            // For single generation, use the first image
            const firstImage = uploadedImages[0];
            const formData = new FormData();
            formData.append('image', firstImage);

            setUploadingImage(true);
            try {
              // Step 1: Upload to Cloudflare R2
              const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
                method: 'POST',
                body: formData,
              });

              if (!uploadResponse.ok) {
                throw new Error(t('aiGenerator.failedToUploadImage'));
              }

              const uploadResult = await uploadResponse.json();
              imageUrl = uploadResult.url;

              // Step 2: Save to database (store images/XHS images library)
              try {
                const insertResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    image_url: imageUrl,
                    is_active: true
                  }),
                });

                if (!insertResponse.ok) {
                  console.warn('Failed to save image to database, but image URL is available:', imageUrl);
                  // Continue even if database save fails - image URL is still valid
                }
              } catch (dbError) {
                console.warn('Database save error:', dbError);
                // Continue even if database save fails - image URL is still valid
              }
            } catch (error) {
              console.error('Error uploading image:', error);
              setError(t('aiGenerator.failedToUploadImage'));
              setGenerating(false);
              setUploadingImage(false);
              return;
            } finally {
              setUploadingImage(false);
            }

            if (!imageUrl) {
              setGenerating(false);
              return;
            }
          } else if (selectedXhsImage || (selectedXhsImages && selectedXhsImages.length > 0)) {
            // Use selected XHS image (single or first from multiple selection)
            imageUrl = selectedXhsImage ? selectedXhsImage.image_url : selectedXhsImages[0].image_url;
          }
        }

        const prompt = generatePrompt(selectedPlatform, businessInfo, imageUrl);

        // Filter businessInfo to only include relevant fields for each platform
        let filteredBusinessInfo;
        if (selectedPlatform === 'google' || selectedPlatform === 'facebook') {
          // For Google and Facebook, only send basic fields
          filteredBusinessInfo = {
            name: businessInfo.name,
            location: businessInfo.location,
            businessNature: businessInfo.businessNature,
            sellingPoint: businessInfo.sellingPoint,
            language: businessInfo.language,
            category: businessInfo.category
          };
        } else {
          // For XHS, send only essential fields (excluding removed business fields)
          filteredBusinessInfo = {
            name: businessInfo.name,
            location: businessInfo.location,
            businessNature: businessInfo.businessNature,
            sellingPoint: businessInfo.sellingPoint,
            hashtags: businessInfo.hashtags,
            language: businessInfo.language,
            category: businessInfo.category
          };
        }

        const requestBody = {
          businessInfo: filteredBusinessInfo,
          platform: selectedPlatform,
          prompt: prompt,
          imageUrl: imageUrl // Include image URL for image-based generation
        };

        console.log('🚀 Sending generation request:', {
          platform: selectedPlatform,
          hasImageUrl: !!imageUrl,
          imageUrl: imageUrl,
          businessInfo: businessInfo
        });

        const response = await fetch(`${API_ENDPOINTS.GENERATE_CONTENT}?brand=${getCurrentBrand()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(t('aiGenerator.failedToGenerateContentSingle'));
        }

        const data = await response.json();
        setGeneratedContent(data);

        // Clear only uploaded images after successful generation, but keep selected XHS images
        // This allows users to regenerate with the same selected images from the library
        if (uploadedImage || (uploadedImages && uploadedImages.length > 0)) {
          setUploadedImage(null);
          setUploadedImages([]);
          setImagePreview(null);
          setImagePreviews([]);
          // Keep selectedXhsImages and selectedXhsImage so they can be reused for regeneration
        }

        // Refresh platform content to show the new generated content and update counter
        fetchPlatformContent(selectedPlatform);
        fetchQuota();

        // Save to history handled by useEffect
      }

    } catch (err) {
      console.error('Error generating content:', err);
      setError(err.message);
      // Reset batch progress on error
      setBatchProgress({
        isBatchGenerating: false,
        current: 0,
        total: 0,
        completed: 0,
        failed: 0
      });
    } finally {
      setGenerating(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [field]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [field]: false }));
      }, 2000);
    });
  };

  // Handle image selection (single - for backward compatibility)
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle multiple image selection (handles both single and multiple files)
  const handleMultipleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Append new files to existing uploaded images instead of replacing
      setUploadedImages(prev => [...prev, ...files]);

      // Clear single image states
      setUploadedImage(null);
      setImagePreview(null);
      setSelectedXhsImage(null);
      // Don't clear selectedXhsImages - allow both uploaded and library images to coexist

      // Generate previews for all selected files
      const readers = files.map(file => {
        const reader = new FileReader();
        return new Promise((resolve) => {
          reader.onload = (e) => resolve({ file, preview: e.target.result });
          reader.readAsDataURL(file);
        });
      });

      Promise.all(readers).then(previews => {
        // Append new previews to existing ones
        setImagePreviews(prev => [...prev, ...previews]);
      });

      // Reset the input to allow selecting the same files again
      event.target.value = '';
    }
  };

  const handleEditImageUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setEditImageFile(file);
    setEditSelectedLibraryImage(null);
    setEditDialog(prev => ({ ...prev, image_url: '' }));
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const clearEditImage = () => {
    setEditImageFile(null);
    setEditImagePreview('');
    setEditSelectedLibraryImage(null);
    setEditDialog(prev => ({ ...prev, image_url: '' }));
  };

  const uploadImageFileToLibrary = async (file) => {
    if (!file) return null;
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);

      const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error(t('aiGenerator.failedToUploadImage'));
      }

      const uploadResult = await uploadResponse.json();
      const imageUrl = uploadResult.url;

      try {
        await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_url: imageUrl, is_active: true })
        });
      } catch (dbError) {
        console.warn('Database save error during edit upload:', dbError);
      }

      return imageUrl;
    } catch (error) {
      console.error('Error uploading edit image:', error);
      setError(t('aiGenerator.failedToUploadImage'));
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image upload to R2 and save to database (single image)
  const handleImageUpload = async () => {
    if (!uploadedImage) return null;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', uploadedImage);

      // Step 1: Upload to Cloudflare R2
      const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error(t('aiGenerator.failedToUploadImage'));
      }

      const uploadResult = await uploadResponse.json();
      const imageUrl = uploadResult.url;

      // Step 2: Save to database (store images/XHS images library)
      try {
        const insertResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image_url: imageUrl,
            is_active: true
          }),
        });

        if (!insertResponse.ok) {
          console.warn('Failed to save image to database, but image URL is available:', imageUrl);
          // Continue even if database save fails - image URL is still valid
        }
      } catch (dbError) {
        console.warn('Database save error:', dbError);
        // Continue even if database save fails - image URL is still valid
      }

      return imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(t('aiGenerator.failedToUploadImage'));
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle multiple image uploads to R2 and save to database
  const handleMultipleImageUpload = async () => {
    if (!uploadedImages || uploadedImages.length === 0) return [];

    try {
      setUploadingImage(true);
      const uploadPromises = uploadedImages.map(async (file) => {
        // Step 1: Upload to Cloudflare R2
        const formData = new FormData();
        formData.append('image', file);

        const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(t('aiGenerator.failedToUploadImageFile', { filename: file.name }));
        }

        const uploadResult = await uploadResponse.json();
        const imageUrl = uploadResult.url;

        // Step 2: Save to database (store images/XHS images library)
        try {
          const insertResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image_url: imageUrl,
              is_active: true
            }),
          });

          if (!insertResponse.ok) {
            console.warn(`Failed to save ${file.name} to database, but image URL is available:`, imageUrl);
            // Continue even if database save fails - image URL is still valid
          }
        } catch (dbError) {
          console.warn(`Database save error for ${file.name}:`, dbError);
          // Continue even if database save fails - image URL is still valid
        }

        return imageUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(t('aiGenerator.failedToUploadImages'));
      return [];
    } finally {
      setUploadingImage(false);
    }
  };

  // Clear image selection
  const clearImage = () => {
    setUploadedImage(null);
    setUploadedImages([]);
    setImagePreview(null);
    setImagePreviews([]);
    setSelectedXhsImage(null);
    setSelectedXhsImages([]);
  };

  // Handle XHS image selection (toggle selection for multiple)
  const handleXhsImageSelect = useCallback((image) => {
    if (imageSelectorPurpose === 'edit') {
      const isSelected = selectedXhsImages.some(img => img.id === image.id);
      if (isSelected) {
        setSelectedXhsImages([]);
        setEditSelectedLibraryImage(null);
      } else {
        setSelectedXhsImages([image]);
        setEditSelectedLibraryImage(image);
      }
      setSelectedXhsImage(image);
      return;
    }

    // Toggle selection for multiple images (generator)
    setSelectedXhsImages(prev => {
      const isSelected = prev.some(img => img.id === image.id);
      if (isSelected) {
        return prev.filter(img => img.id !== image.id);
      }
      return [...prev, image];
    });

    setSelectedXhsImage(image);
  }, [imageSelectorPurpose, selectedXhsImages, setSelectedXhsImages, setEditSelectedLibraryImage, setSelectedXhsImage]);

  // Stable handler for image selection to prevent re-renders of all cards
  const handleXhsImageSelectRef = React.useRef(handleXhsImageSelect);

  useEffect(() => {
    handleXhsImageSelectRef.current = handleXhsImageSelect;
  }, [handleXhsImageSelect]);

  const stableHandleXhsImageSelect = useCallback((image) => {
    handleXhsImageSelectRef.current(image);
  }, []);

  // Confirm XHS image selection (close dialog and update previews)
  const handleConfirmXhsImageSelection = () => {
    if (selectedXhsImages.length === 0) return;

    if (imageSelectorPurpose === 'edit') {
      const selectedImage = selectedXhsImages[0];
      setEditSelectedLibraryImage(selectedImage);
      setEditImageFile(null);
      setEditImagePreview(selectedImage.image_url);
      setEditDialog(prev => ({ ...prev, image_url: selectedImage.image_url }));
      setSelectedXhsImages([]);
      setImageSelectorOpen(false);
      setImageSelectorPurpose('generator');
      return;
    }

    // Generator flow
    setUploadedImages([]);
    setImagePreviews([]);
    setUploadedImage(null);
    setImagePreview(null);

    const previews = selectedXhsImages.map(img => ({
      file: null,
      preview: img.image_url
    }));
    setImagePreviews(previews);

    setImageSelectorOpen(false);
  };

  // Open image selector
  const handleOpenImageSelector = (context = 'generator') => {
    setImageSelectorPurpose(context);
    if (context === 'edit') {
      setSelectedXhsImages(editSelectedLibraryImage ? [editSelectedLibraryImage] : []);
      setSelectedXhsImage(editSelectedLibraryImage || null);
    }
    setImageSelectorOpen(true);
    if (xhsImages.length === 0) {
      fetchXhsImages();
    }
  };

  // Close image selector and reset selection if cancelled
  const handleCloseImageSelector = () => {
    setSelectedXhsImages([]);
    setImageSelectorOpen(false);
    setImageSelectorPurpose('generator');
  };

  // Open generator dialog
  const handleOpenGenerator = () => {
    setGeneratorOpen(true);
    setGeneratedContent(null);
  };

  // Close generator dialog
  const handleCloseGenerator = () => {
    setGeneratorOpen(false);
    setGeneratedContent(null);
  };


  const handleXhsItemsPerPageChange = (e) => {
    setXhsItemsPerPage(e.target.value);
    setXhsImagePage(1); // Reset to page 1
  };

  return (
    <>
      <style>
        {`
          .card-overlay {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .MuiCard-root:hover .card-overlay {
            opacity: 1;
          }
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-10px) rotate(1deg); }
            50% { transform: translateY(-20px) rotate(0deg); }
            75% { transform: translateY(-10px) rotate(-1deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .shimmer {
            position: relative;
            overflow: hidden;
          }
          .shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: shimmer 2s infinite;
          }
          .pulse-animation {
            animation: pulse 2s ease-in-out infinite;
          }
          .gradient-animation {
            background-size: 200% 200%;
            animation: gradientShift 5s ease infinite;
          }
        `}
      </style>
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 }
        }}
      >
        {/* Main Content Card Container */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          {/* AI Generator Header Section */}
          <Paper
            elevation={0}
            className="gradient-animation"
            sx={{
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 50%, #2c3e50 100%)',
              color: 'white',
              borderRadius: 0,
              p: { xs: 3, md: 5 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: 'none'
            }}
          >
            {/* Animated Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                backgroundImage: `
              radial-gradient(circle at 20% 20%, white 2px, transparent 2px),
              radial-gradient(circle at 80% 80%, white 2px, transparent 2px),
              radial-gradient(circle at 40% 60%, white 1px, transparent 1px)
            `,
                backgroundSize: '80px 80px, 100px 100px, 50px 50px',
                animation: 'float 25s ease-in-out infinite'
              }}
            />

            {/* Floating Orbs */}
            <Box
              sx={{
                position: 'absolute',
                top: '15%',
                right: '10%',
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(15px)',
                animation: 'float 18s ease-in-out infinite reverse'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                left: '8%',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(12px)',
                animation: 'float 15s ease-in-out infinite'
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ flexGrow: 1, mb: { xs: 3, md: 0 }, width: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 2, flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: { xs: 48, sm: 56, md: 72 },
                    height: { xs: 48, sm: 56, md: 72 },
                    border: '3px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                    backdropFilter: 'blur(15px)',
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                    '&:hover': {
                      transform: { xs: 'none', sm: 'scale(1.1) rotate(10deg)' },
                      boxShadow: '0 16px 50px rgba(0,0,0,0.3)'
                    }
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: { xs: 24, sm: 28, md: 36 }, color: 'white' }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.75rem', lg: '3.5rem' },
                        textShadow: '0 4px 30px rgba(0,0,0,0.4)',
                        background: 'linear-gradient(45deg, #ffffff 0%, #f0f0ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em',
                        lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {t('aiGenerator.xhsContentGeneratorTitle')}
                    </Typography>
                    <Typography variant="h5" sx={{
                      opacity: 0.95,
                      fontWeight: 500,
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem', lg: '1.3rem' },
                      textShadow: '0 2px 15px rgba(0,0,0,0.3)',
                      letterSpacing: '0.01em',
                      lineHeight: { xs: 1.3, sm: 1.4 },
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      {t('aiGenerator.xhsContentGeneratorSubtitle')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            </Stack>

          </Paper>

          {/* Platform Selection Switcher */}
          <Box sx={{
            width: '100%',
            pt: 0,
            pb: 0
          }}>
            <Paper
              elevation={0}
              sx={{
                mb: 0,
                borderRadius: 0,
                overflow: 'hidden',
                background: '#ffffff',
                border: 'none',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
                  {[
                    {
                      id: 'generator',
                      labelKey: 'aiGenerator.xhsContentGeneratorTab',
                      icon: LanguageIcon,
                      color: '#ff6b6b',
                      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                      glowColor: 'rgba(255, 107, 107, 0.3)',
                      accentColor: '#ff8a80'
                    },
                    {
                      id: 'images',
                      labelKey: 'aiGenerator.xhsImagesTab',
                      icon: ImageIcon,
                      color: '#ff6b6b',
                      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                      glowColor: 'rgba(255, 107, 107, 0.3)',
                      accentColor: '#ff8a80'
                    },
                    ...(canManageVehicleModels ? [{
                      id: 'models',
                      labelKey: 'aiGenerator.vehicleModelsTab',
                      defaultLabel: 'Vehicle Models',
                      icon: DirectionsCarIcon,
                      color: '#ff6b6b',
                      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                      glowColor: 'rgba(255, 107, 107, 0.3)',
                      accentColor: '#ff8a80'
                    }] : [])
                  ].map((platform) => {
                    const IconComponent = platform.icon;
                    const currentHash = location.hash || '#generator';
                    const isSelected = currentHash === `#${platform.id}` || (currentHash === '' && platform.id === 'generator');
                    return (
                      <Button
                        key={platform.id}
                        onClick={() => platform.isExternal ? navigate(platform.path) : navigate(`/xhs#${platform.id}`)}
                        sx={{
                          flex: 1,
                          py: { xs: 2, sm: 2.5, md: 3.25 },
                          px: { xs: 1.5, sm: 2.5, md: 4 },
                          borderRadius: 0,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.05rem' },
                          color: isSelected ? 'white' : '#2c3e50',
                          background: isSelected ? platform.gradient : '#ffffff',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isSelected ? `0 4px 16px ${platform.glowColor}, 0 2px 8px rgba(255, 107, 107, 0.2)` : 'none',
                          border: 'none',
                          minHeight: { xs: 56, md: 68 },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: isSelected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            zIndex: 1
                          },
                          '&:hover': {
                            background: isSelected ? platform.gradient : 'rgba(255, 107, 107, 0.06)',
                            boxShadow: isSelected ? `0 6px 20px ${platform.glowColor}, 0 3px 10px rgba(255, 107, 107, 0.25)` : '0 2px 8px rgba(0, 0, 0, 0.04)',
                            transform: isSelected ? 'translateY(-1px)' : 'none',
                            '&::before': {
                              opacity: isSelected ? 1 : 0
                            },
                            '& .platform-icon': {
                              transform: 'scale(1.08)',
                              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }
                          },
                          '&:active': {
                            transform: 'scale(0.98) translateY(0)',
                            transition: 'all 0.1s ease'
                          }
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={{ xs: 1, sm: 1.5 }}
                          sx={{ position: 'relative', zIndex: 2, width: '100%', flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}
                        >
                          <IconComponent
                            className="platform-icon"
                            sx={{
                              fontSize: { xs: 18, sm: 20, md: 23 },
                              color: 'inherit',
                              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                              filter: isSelected ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' : 'none',
                              flexShrink: 0
                            }}
                          />
                          <Typography
                            className="platform-text"
                            variant="body1"
                            sx={{
                              fontWeight: isSelected ? 700 : 600,
                              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem', lg: 'inherit' },
                              letterSpacing: '0.015em',
                              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                              textShadow: isSelected ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0,
                              lineHeight: 1.2
                            }}
                          >
                            {t(platform.labelKey, { defaultValue: platform.defaultLabel || platform.labelKey })}
                          </Typography>
                        </Stack>
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          </Box>

          {/* Content Section */}
          <Box sx={{
            p: { xs: 2, sm: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.3) 50%, transparent 100%)'
            }
          }}>

            {/* Content Counter */}
            {selectedPlatform && (
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.05) 0%, rgba(255, 107, 107, 0.02) 100%)',
                  border: '1px solid rgba(255, 107, 107, 0.1)',
                }}
              >
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 2, sm: 2 }}>
                    {/* Left: Platform Info */}
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <Avatar sx={{
                        width: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                        flexShrink: 0
                      }}>
                        <LanguageIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'white' }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{
                          color: '#2c3e50',
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          lineHeight: 1.2
                        }}>
                          {t('aiGenerator.xiaohongshuPosts')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6c757d', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('aiGenerator.contentCounter')}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Right: Progress & Count */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 1.5, sm: 3 }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      {/* Progress Bar */}
                      <Box sx={{ width: { xs: '100%', sm: 120 }, order: { xs: 2, sm: 1 } }}>
                        <Box
                          sx={{
                            width: '100%',
                            height: { xs: 8, sm: 6 },
                            borderRadius: 3,
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.min((platformTotalCount / 1500) * 100, 100)}%`,
                              height: '100%',
                              borderRadius: 3,
                              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                              transition: 'width 0.6s ease',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Count Display */}
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ order: { xs: 1, sm: 2 } }}>
                        <Typography variant="h5" sx={{
                          color: '#2c3e50',
                          fontWeight: 700,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          lineHeight: 1
                        }}>
                          {platformTotalCount.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: '#6c757d',
                          fontWeight: 500,
                          fontSize: '0.9rem'
                        }}>
                          / 1,500
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* Error Alert */}
            {error && (
              <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.25rem'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError(null)}
                    >
                      <ErrorIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              </Slide>
            )}


            {/* Loading State */}
            {(loading || platformLoading) && (
              <Fade in timeout={300}>
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                  {[...Array(8)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                      <Card
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          border: '2px solid transparent',
                          background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box',
                          animation: 'float 6s ease-in-out infinite',
                          animationDelay: `${index * 0.2}s`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 30px rgba(102, 126, 234, 0.25)'
                          }
                        }}
                      >
                        <Box className="shimmer">
                          <Skeleton
                            variant="rectangular"
                            height={240}
                            sx={{
                              background: 'linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)',
                              backgroundSize: '200% 100%',
                              animation: 'loading 1.8s infinite',
                              borderBottom: '1px solid #e9ecef'
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Skeleton variant="text" height={24} width="60%" sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={16} width="80%" sx={{ mb: 2 }} />
                            <Stack direction="row" spacing={1}>
                              <Skeleton variant="rectangular" height={24} width={60} sx={{ borderRadius: 2 }} />
                              <Skeleton variant="rectangular" height={24} width={70} sx={{ borderRadius: 2 }} />
                            </Stack>
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
                          <Skeleton variant="rectangular" height={36} width={120} sx={{ borderRadius: 2 }} />
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Fade>
            )}

            {/* Content Grid */}
            {!loading && !platformLoading && (
              <Fade in timeout={500}>
                <Box sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.2) 50%, transparent 100%)',
                    borderRadius: '2px'
                  }
                }}>
                  {/* Platform Content Grid */}
                  {selectedPlatform && (
                    <Box sx={{ mb: 4 }}>
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                        spacing={{ xs: 2, lg: 0 }}
                        sx={{ mb: { xs: 2, sm: 3 } }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            color: '#2c3e50',
                            fontWeight: 600,
                            textAlign: { xs: 'center', lg: 'left' },
                            fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 },
                            mb: { xs: 1, lg: 0 }
                          }}
                        >
                          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                            {t('aiGenerator.xiaohongshuPosts')}
                          </Box>
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 1.5, sm: 1, md: 2 }}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          sx={{ width: { xs: '100%', lg: 'auto' }, flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' } }}
                        >
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: { xs: '100%', sm: 140, md: 160 },
                              width: { xs: '100%', sm: 'auto' },
                              flexShrink: { xs: 0, sm: 1, md: 0 },
                              '& .MuiInputLabel-root': {
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                '&.Mui-focused': {
                                  color: '#667eea'
                                }
                              }
                            }}
                          >
                            <InputLabel>{t('aiGenerator.contentFilter')}</InputLabel>
                            <Select
                              value={contentFilter}
                              label={t('aiGenerator.contentFilter')}
                              onChange={(e) => setContentFilter(e.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 3,
                                  backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                  border: '2px solid rgba(102, 126, 234, 0.2)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: 'rgba(102, 126, 234, 0.4)',
                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                  },
                                  '&.Mui-focused': {
                                    borderColor: '#667eea',
                                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                                  }
                                },
                                '& .MuiSelect-select': {
                                  color: '#2c3e50',
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  py: 1.5
                                }
                              }}
                            >
                              <MenuItem
                                value="active"
                                sx={{
                                  color: '#27ae60',
                                  fontWeight: 600,
                                  '&:hover': {
                                    backgroundColor: 'rgba(39, 174, 96, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(39, 174, 96, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(39, 174, 96, 0.16)'
                                    }
                                  }
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: '#27ae60' }} />
                                {t('aiGenerator.activeOnly')}
                              </MenuItem>
                              <MenuItem
                                value="inactive"
                                sx={{
                                  color: '#e74c3c',
                                  fontWeight: 600,
                                  '&:hover': {
                                    backgroundColor: 'rgba(231, 76, 60, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(231, 76, 60, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(231, 76, 60, 0.16)'
                                    }
                                  }
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: 16, mr: 1, color: '#e74c3c' }} />
                                {t('aiGenerator.inactiveOnly')}
                              </MenuItem>
                              <MenuItem
                                value="all"
                                sx={{
                                  color: '#667eea',
                                  fontWeight: 600,
                                  '&:hover': {
                                    backgroundColor: 'rgba(102, 126, 234, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(102, 126, 234, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(102, 126, 234, 0.16)'
                                    }
                                  }
                                }}
                              >
                                <AutoAwesomeIcon sx={{ fontSize: 16, mr: 1, color: '#667eea' }} />
                                {t('aiGenerator.allItems')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: { xs: '100%', sm: 160, md: 180 },
                              width: { xs: '100%', sm: 'auto' },
                              flexShrink: { xs: 0, sm: 1, md: 0 },
                              '& .MuiInputLabel-root': {
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                '&.Mui-focused': {
                                  color: '#667eea'
                                }
                              }
                            }}
                          >
                            <InputLabel
                              sx={{
                                color: '#ff6b6b',
                                fontWeight: 600,
                                '&.Mui-focused': {
                                  color: '#ff6b6b'
                                }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CategoryIcon sx={{ fontSize: 18 }} />
                                <span>Category Filter</span>
                              </Stack>
                            </InputLabel>
                            <Select
                              value={categoryFilter || 'all'}
                              label="Category Filter"
                              onChange={(e) => setCategoryFilter(e.target.value === 'all' ? '' : e.target.value)}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected || selected === 'all') {
                                  return (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <FilterListIcon sx={{ fontSize: 18, color: '#ff6b6b' }} />
                                      <span style={{ color: '#ff6b6b', fontWeight: 600 }}>All Categories</span>
                                    </Stack>
                                  );
                                }
                                return (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CategoryIcon sx={{ fontSize: 18, color: '#ff6b6b' }} />
                                    <span style={{ color: '#2c3e50', fontWeight: 600 }}>{selected}</span>
                                  </Stack>
                                );
                              }}
                              startAdornment={
                                !categoryFilter || categoryFilter === 'all' ? null : (
                                  <CategoryIcon sx={{ fontSize: 18, color: '#ff6b6b', mr: 1 }} />
                                )
                              }
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 3,
                                  backgroundColor: 'rgba(255, 107, 107, 0.06)',
                                  border: '2px solid rgba(255, 107, 107, 0.25)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: '0 2px 8px rgba(255, 107, 107, 0.1)',
                                  '&:hover': {
                                    borderColor: 'rgba(255, 107, 107, 0.5)',
                                    backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.15)',
                                    transform: 'translateY(-1px)'
                                  },
                                  '&.Mui-focused': {
                                    borderColor: '#ff6b6b',
                                    backgroundColor: 'rgba(255, 107, 107, 0.12)',
                                    boxShadow: '0 0 0 4px rgba(255, 107, 107, 0.15), 0 4px 16px rgba(255, 107, 107, 0.2)'
                                  }
                                },
                                '& .MuiSelect-select': {
                                  color: '#2c3e50',
                                  fontWeight: 600,
                                  fontSize: '0.95rem',
                                  py: 1.5,
                                  display: 'flex',
                                  alignItems: 'center'
                                },
                                '& .MuiSelect-icon': {
                                  color: '#ff6b6b',
                                  fontSize: '1.5rem'
                                }
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    borderRadius: 3,
                                    mt: 1,
                                    boxShadow: '0 8px 24px rgba(255, 107, 107, 0.2)',
                                    border: '1px solid rgba(255, 107, 107, 0.2)',
                                    '& .MuiMenuItem-root': {
                                      borderRadius: 2,
                                      mx: 1,
                                      my: 0.5,
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                        transform: 'translateX(4px)'
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: 'rgba(255, 107, 107, 0.15)',
                                        color: '#ff6b6b',
                                        fontWeight: 600,
                                        '&:hover': {
                                          backgroundColor: 'rgba(255, 107, 107, 0.2)'
                                        }
                                      }
                                    }
                                  }
                                }
                              }}
                            >
                              <MenuItem value="all">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <FilterListIcon sx={{ fontSize: 18, color: '#ff6b6b' }} />
                                  <em style={{ fontStyle: 'normal', fontWeight: 600, color: '#ff6b6b' }}>All Categories</em>
                                </Stack>
                              </MenuItem>
                              {categories.map((cat) => (
                                <MenuItem key={cat} value={cat}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CategoryIcon sx={{ fontSize: 16, color: '#ff6b6b', opacity: 0.7 }} />
                                    <span>{cat}</span>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {(() => {
                            const inactiveCount = platformContent.filter(item => item.is_active === false).length;
                            return inactiveCount > 0 ? (
                              <Button
                                variant="contained"
                                startIcon={activatingAll ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                onClick={handleActivateAllInactive}
                                disabled={activatingAll}
                                sx={{
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8c 100%)',
                                    transform: { xs: 'none', sm: 'translateY(-2px)' },
                                    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                                  },
                                  '&:disabled': {
                                    background: 'linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%)',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                  },
                                  px: { xs: 2, sm: 2 },
                                  py: { xs: 1, sm: 1 },
                                  borderRadius: { xs: 1.5, sm: 2 },
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  minHeight: { xs: 40, sm: 44 },
                                  minWidth: { xs: '100%', sm: 'auto' },
                                  width: { xs: '100%', sm: 'auto' },
                                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                                  transition: 'all 0.3s ease',
                                  '& .MuiButton-startIcon': {
                                    marginRight: { xs: 1, sm: 1.5 },
                                    '& > *:nth-of-type(1)': {
                                      fontSize: { xs: '1rem', sm: '1.1rem' }
                                    }
                                  }
                                }}
                              >
                                {activatingAll ? (
                                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {t('aiGenerator.activatingAll') || 'Activating...'}
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: 'nowrap', sm: 'wrap' }, justifyContent: 'center', minWidth: 0 }}>
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'nowrap', sm: 'normal' }, flex: { xs: 1, sm: 'none' }, minWidth: 0 }}>
                                      {t('aiGenerator.activateAllInactive') || 'Activate All'}
                                    </Box>
                                    <Chip
                                      label={inactiveCount}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        minWidth: 20,
                                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        flexShrink: 0,
                                        '& .MuiChip-label': {
                                          px: 0.75,
                                          py: 0
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                              </Button>
                            ) : null;
                          })()}
                          <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={handleOpenGenerator}
                            sx={{
                              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #ee5a52 0%, #e04a42 100%)',
                                transform: { xs: 'none', sm: 'translateY(-2px)' },
                              },
                              px: { xs: 2, sm: 2, md: 4 },
                              py: { xs: 1, sm: 1, md: 1.5 },
                              borderRadius: { xs: 1.5, sm: 2 },
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                              minHeight: { xs: 40, sm: 44, md: 48 },
                              minWidth: { xs: '100%', sm: 'auto' },
                              width: { xs: '100%', sm: 'auto' },
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              transition: 'all 0.3s ease',
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              '& .MuiButton-startIcon': {
                                marginRight: { xs: 1, sm: 1.5 },
                                flexShrink: 0,
                                '& > *:nth-of-type(1)': {
                                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.25rem' }
                                }
                              }
                            }}
                          >
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                              {t('aiGenerator.generateContent')} XHS
                            </Box>
                          </Button>
                        </Stack>
                      </Stack>
                      {/* Bulk Action Toolbar */}
                      {selectedIds.size > 0 && (
                        <Fade in>
                          <Paper
                            elevation={3}
                            sx={{
                              position: 'sticky',
                              top: { xs: 70, sm: 80 },
                              zIndex: 10,
                              mb: 3,
                              p: 2,
                              borderRadius: 3,
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 107, 107, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              gap: 2,
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                              background: 'linear-gradient(to right, rgba(255, 255, 255, 0.9), rgba(255, 107, 107, 0.05))',
                              animation: 'shimmer-gradient 3s ease-in-out infinite'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                              <Chip
                                label={`${selectedIds.size} ${selectedIds.size === 1 ? 'item' : 'items'}`}
                                sx={{
                                  bgcolor: '#ff6b6b',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  height: { xs: 24, sm: 28 },
                                  px: { xs: 0.75, sm: 1 },
                                  '& .MuiChip-label': {
                                    px: { xs: 1, sm: 1.5 }
                                  }
                                }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#616161', fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
                                selected
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: { xs: 0, sm: 1 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <Tooltip title="Select all (across all pages)" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={selectAllOnPage}
                                    disabled={platformContent.length === 0}
                                    sx={{
                                      color: '#d32f2f', // Darker red
                                      bgcolor: 'rgba(211, 47, 47, 0.1)',
                                      border: '1px solid rgba(211, 47, 47, 0.3)',
                                      borderRadius: '50%',
                                      width: 36,
                                      height: 36,
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '&:hover': {
                                        bgcolor: 'rgba(211, 47, 47, 0.2)',
                                        borderColor: '#d32f2f',
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                                      }
                                    }}
                                  >
                                    <SelectAllIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Select all on current page" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={selectAllOnCurrentPage}
                                    disabled={platformContent.length === 0}
                                    sx={{
                                      color: '#1976d2', // Darker blue
                                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                                      border: '1px solid rgba(25, 118, 210, 0.3)',
                                      borderRadius: '50%',
                                      width: 36,
                                      height: 36,
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '&:hover': {
                                        bgcolor: 'rgba(25, 118, 210, 0.2)',
                                        borderColor: '#1976d2',
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                                      }
                                    }}
                                  >
                                    <PlaylistAddCheckIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Clear selection" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={clearSelection}
                                    sx={{
                                      color: '#757575',
                                      bgcolor: 'rgba(117, 117, 117, 0.08)',
                                      border: '1px solid rgba(117, 117, 117, 0.2)',
                                      borderRadius: '50%',
                                      width: 36,
                                      height: 36,
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '&:hover': {
                                        bgcolor: 'rgba(117, 117, 117, 0.16)',
                                        borderColor: '#757575',
                                        transform: 'scale(1.1) rotate(-5deg)',
                                        boxShadow: '0 4px 12px rgba(117, 117, 117, 0.3)'
                                      }
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
                              {(() => {
                                // Check status of selected items
                                const selectedItems = platformContent.filter(item => selectedIds.has(item.id));
                                const hasActiveItems = selectedItems.some(item => item.is_active !== false);
                                const hasInactiveItems = selectedItems.some(item => item.is_active === false);

                                return (
                                  <>
                                    {hasActiveItems && (
                                      <Button
                                        size="medium"
                                        variant="contained"
                                        onClick={handleBulkDeactivate}
                                        disabled={bulkLoading}
                                        startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
                                        sx={{
                                          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                          color: 'white',
                                          fontWeight: 600,
                                          textTransform: 'none',
                                          px: { xs: 1.5, sm: 2.5 },
                                          py: { xs: 0.75, sm: 1 },
                                          borderRadius: 2,
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                          minWidth: { xs: 'auto', sm: 'auto' },
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                                            boxShadow: '0 4px 16px rgba(255, 152, 0, 0.4)'
                                          },
                                          '&:disabled': {
                                            background: '#e0e0e0',
                                            color: '#9e9e9e',
                                            boxShadow: 'none'
                                          },
                                          '& .MuiButton-startIcon': {
                                            marginRight: { xs: 0.5, sm: 1 }
                                          }
                                        }}
                                      >
                                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                          {t('aiGenerator.bulkDeactivate') || 'Deactivate Selected'}
                                        </Box>
                                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                          {t('aiGenerator.bulkDeactivate') ? t('aiGenerator.bulkDeactivate').split(' ')[0] : 'Deactivate'}
                                        </Box>
                                      </Button>
                                    )}

                                    {hasInactiveItems && (
                                      <Button
                                        size="medium"
                                        variant="contained"
                                        onClick={handleBulkActivate}
                                        disabled={bulkLoading}
                                        startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                                        sx={{
                                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                          color: 'white',
                                          fontWeight: 600,
                                          textTransform: 'none',
                                          px: { xs: 1.5, sm: 2.5 },
                                          py: { xs: 0.75, sm: 1 },
                                          borderRadius: 2,
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                          minWidth: { xs: 'auto', sm: 'auto' },
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                                            boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)'
                                          },
                                          '&:disabled': {
                                            background: '#e0e0e0',
                                            color: '#9e9e9e',
                                            boxShadow: 'none'
                                          },
                                          '& .MuiButton-startIcon': {
                                            marginRight: { xs: 0.5, sm: 1 }
                                          }
                                        }}
                                      >
                                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                          {t('aiGenerator.bulkActivate') || 'Activate Selected'}
                                        </Box>
                                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                          {t('aiGenerator.bulkActivate') ? t('aiGenerator.bulkActivate').split(' ')[0] : 'Activate'}
                                        </Box>
                                      </Button>
                                    )}

                                    <Button
                                      size="medium"
                                      variant="contained"
                                      onClick={handleBulkDelete}
                                      disabled={bulkLoading}
                                      startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                                      sx={{
                                        background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                        color: 'white',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: { xs: 1.5, sm: 2.5 },
                                        py: { xs: 0.75, sm: 1 },
                                        borderRadius: 2,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                        minWidth: { xs: 'auto', sm: 'auto' },
                                        '&:hover': {
                                          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                          transform: { xs: 'none', sm: 'translateY(-2px)' },
                                          boxShadow: '0 4px 16px rgba(244, 67, 54, 0.4)'
                                        },
                                        '&:disabled': {
                                          background: '#e0e0e0',
                                          color: '#9e9e9e',
                                          boxShadow: 'none'
                                        },
                                        '& .MuiButton-startIcon': {
                                          marginRight: { xs: 0.5, sm: 1 }
                                        }
                                      }}
                                    >
                                      <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                        {t('aiGenerator.bulkDelete') || 'Delete Selected'}
                                      </Box>
                                      <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                        {t('aiGenerator.bulkDelete') ? t('aiGenerator.bulkDelete').split(' ')[0] : 'Delete'}
                                      </Box>
                                    </Button>
                                  </>
                                );
                              })()}
                            </Box>
                          </Paper>
                        </Fade>
                      )}

                      {(() => {
                        const filteredContent = platformContent.filter(item => {
                          // Filter by active/inactive status
                          if (contentFilter === 'active' && item.is_active === false) return false;
                          if (contentFilter === 'inactive' && item.is_active !== false) return false;

                          // Filter by category
                          if (categoryFilter) {
                            // If filtering by a specific category, exclude items without that category
                            if (!item.category || item.category !== categoryFilter) return false;
                          }

                          return true;
                        });

                        // Pagination logic
                        const totalFilteredCount = filteredContent.length;
                        const totalPages = Math.ceil(totalFilteredCount / contentItemsPerPage);
                        const startIndex = (contentPage - 1) * contentItemsPerPage;
                        const endIndex = startIndex + contentItemsPerPage;
                        const paginatedContent = filteredContent.slice(startIndex, endIndex);

                        return filteredContent.length > 0 ? (
                          <>
                            <Box sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(2, minmax(0, 1fr))' },
                              gap: { xs: 2, sm: 2.5, md: 3, lg: 4 },
                              alignItems: 'stretch',
                              gridAutoRows: '1fr'
                            }}>
                              {paginatedContent.map((item, index) => (
                                <Box key={item.id}>
                                  <Zoom in timeout={300 + (index * 100)}>
                                    <Card
                                      sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        border: selectedIds.has(item.id) ? '2px solid #ff6b6b' : '2px solid transparent',
                                        background: selectedIds.has(item.id)
                                          ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%) border-box'
                                          : 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box',
                                        position: 'relative',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                          transform: { xs: 'none', sm: 'translateY(-12px) scale(1.03)' },
                                          boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 25px 50px rgba(102, 126, 234, 0.35)' },
                                        }
                                      }}
                                      onClick={() => toggleSelect(item.id)}
                                    >
                                      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                          {/* Title + Status + Edit */}
                                          <Stack direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-start', md: 'stretch', lg: 'flex-start' }} justifyContent="space-between" sx={{ mb: 0.5, gap: { xs: 1, sm: 1.5 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1 }, overflow: 'hidden', flex: 1, minWidth: 0 }}>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleSelect(item.id);
                                                }}
                                                sx={{
                                                  color: selectedIds.has(item.id) ? '#ff6b6b' : '#bdbdbd',
                                                  p: { xs: 0.25, sm: 0.5 },
                                                  flexShrink: 0,
                                                  mt: { xs: 0.25, sm: 0 },
                                                  '&:hover': {
                                                    bgcolor: 'rgba(255, 107, 107, 0.08)'
                                                  }
                                                }}
                                              >
                                                {selectedIds.has(item.id) ? (
                                                  <CheckBoxIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ff6b6b' }} />
                                                ) : (
                                                  <CheckBoxOutlineBlankIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                                )}
                                              </IconButton>
                                              {item.title && (
                                                <Typography
                                                  variant="h6"
                                                  sx={{
                                                    color: '#667eea',
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: { xs: 2, sm: 2, md: 1 },
                                                    WebkitBoxOrient: 'vertical',
                                                    lineHeight: { xs: 1.4, sm: 1.4, md: 1.5 },
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    flex: 1,
                                                    minWidth: 0,
                                                    hyphens: 'auto'
                                                  }}
                                                >
                                                  {item.title}
                                                </Typography>
                                              )}
                                            </Box>
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
                                              sx={{
                                                width: { xs: '100%', sm: 'auto', md: '100%', lg: 'auto' },
                                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                                                alignItems: { xs: 'center', sm: 'center' },
                                                gap: { xs: 0.5, sm: 0.75, md: 1 },
                                                flexShrink: 0,
                                                '& > *': {
                                                  whiteSpace: { xs: 'normal', sm: 'nowrap' }
                                                }
                                              }}
                                            >
                                              <StatusChip
                                                active={item.is_active !== false}
                                                loading={statusUpdatingId === item.id}
                                                onToggle={() => handleToggleStatus(item)}
                                              />
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                                                onClick={(e) => {
                                                  e.stopPropagation(); // Prevent card click
                                                  const itemCategory = item.category || '';
                                                  setEditDialog({
                                                    open: true,
                                                    id: item.id,
                                                    content: item.content || '',
                                                    title: item.title || '',
                                                    hashtags: item.hashtags || '',
                                                    image_url: item.image_url || '',
                                                    category: itemCategory,
                                                    is_active: item.is_active !== false
                                                  });
                                                }}
                                                sx={{
                                                  minWidth: { xs: 'auto', sm: 'auto' },
                                                  px: { xs: 0.75, sm: 1.5 },
                                                  py: { xs: 0.25, sm: 0.5 },
                                                  height: { xs: 26, sm: 28 },
                                                  borderRadius: 2,
                                                  fontWeight: 600,
                                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                  textTransform: 'none',
                                                  borderColor: '#667eea',
                                                  color: '#667eea',
                                                  flexShrink: 0,
                                                  '&:hover': {
                                                    borderColor: '#5568d3',
                                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                                    color: '#5568d3',
                                                    transform: { xs: 'none', sm: 'translateY(-1px)' },
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
                                                  },
                                                  transition: 'all 0.2s ease',
                                                  '& .MuiButton-startIcon': {
                                                    marginRight: { xs: 0.25, sm: 0.5 },
                                                    marginLeft: 0
                                                  }
                                                }}
                                              >
                                                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                                  {t('aiGenerator.edit')}
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                                  {(t('aiGenerator.edit') || 'Edit').substring(0, Math.min(4, (t('aiGenerator.edit') || 'Edit').length))}
                                                </Box>
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DeleteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                                                onClick={(e) => {
                                                  e.stopPropagation(); // Prevent card click
                                                  handleDeleteContent(item.id);
                                                }}
                                                sx={{
                                                  minWidth: { xs: 'auto', sm: 'auto' },
                                                  px: { xs: 0.75, sm: 1.5 },
                                                  py: { xs: 0.25, sm: 0.5 },
                                                  height: { xs: 26, sm: 28 },
                                                  borderRadius: 2,
                                                  fontWeight: 600,
                                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                  textTransform: 'none',
                                                  borderColor: '#e74c3c',
                                                  color: '#e74c3c',
                                                  flexShrink: 0,
                                                  '&:hover': {
                                                    borderColor: '#c0392b',
                                                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                                    color: '#c0392b',
                                                    transform: { xs: 'none', sm: 'translateY(-1px)' },
                                                    boxShadow: '0 2px 8px rgba(231, 76, 60, 0.2)'
                                                  },
                                                  transition: 'all 0.2s ease',
                                                  '& .MuiButton-startIcon': {
                                                    marginRight: { xs: 0.25, sm: 0.5 },
                                                    marginLeft: 0
                                                  }
                                                }}
                                              >
                                                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                                  {t('aiGenerator.delete')}
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                                  {t('aiGenerator.delete') || 'Delete'}
                                                </Box>
                                              </Button>
                                            </Stack>
                                          </Stack>

                                          {/* XHS Image Preview - Enhanced */}
                                          {item.image_url && (
                                            <Box
                                              sx={{
                                                position: 'relative',
                                                width: '100%',
                                                height: { xs: 180, sm: 240, md: 300, lg: 320 },
                                                borderRadius: { xs: 2, sm: 3 },
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                border: '2px solid rgba(102, 126, 234, 0.1)',
                                                '&:hover': {
                                                  transform: { xs: 'none', sm: 'scale(1.02)' },
                                                  boxShadow: { xs: 'none', sm: '0 12px 35px rgba(102, 126, 234, 0.25)' },
                                                  borderColor: { xs: 'rgba(102, 126, 234, 0.1)', sm: 'rgba(102, 126, 234, 0.3)' },
                                                  '& .image-overlay': {
                                                    opacity: { xs: 0, sm: 1 }
                                                  }
                                                }
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation(); // Prevent card click
                                                setEditDialog({
                                                  open: true,
                                                  id: item.id,
                                                  content: item.content || '',
                                                  title: item.title || '',
                                                  hashtags: item.hashtags || '',
                                                  image_url: item.image_url || '',
                                                  category: item.category || '',
                                                  is_active: item.is_active !== false
                                                });
                                              }}
                                            >
                                              <img
                                                src={item.image_url}
                                                alt={`XHS Post - ${item.title || 'Untitled'}`}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover',
                                                  objectPosition: 'center'
                                                }}
                                              />
                                              {/* Hover Overlay */}
                                              <Box
                                                className="image-overlay"
                                                sx={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  right: 0,
                                                  bottom: 0,
                                                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
                                                  opacity: 0,
                                                  transition: 'opacity 0.3s ease',
                                                  display: 'flex',
                                                  alignItems: 'flex-end',
                                                  justifyContent: 'center',
                                                  pb: 2
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                    px: 2
                                                  }}
                                                >
                                                  {t('aiGenerator.clickToEdit') || 'Click to edit'}
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  position: 'absolute',
                                                  top: 12,
                                                  right: 12,
                                                  bgcolor: 'rgba(0,0,0,0.6)',
                                                  backdropFilter: 'blur(10px)',
                                                  color: 'white',
                                                  borderRadius: 2,
                                                  px: 1.5,
                                                  py: 0.75,
                                                  fontSize: '0.75rem',
                                                  fontWeight: 700,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.75,
                                                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                                  border: '1px solid rgba(255,255,255,0.2)'
                                                }}
                                              >
                                                <Box sx={{ width: 6, height: 6, bgcolor: '#ff6b6b', borderRadius: '50%' }} />
                                                XHS
                                              </Box>
                                            </Box>
                                          )}

                                          <Typography variant="body2" sx={{ color: '#2c3e50', lineHeight: 1.6 }}>
                                            {item.content}
                                          </Typography>
                                          {item.hashtags && (
                                            <Typography variant="caption" sx={{ color: '#667eea', fontSize: '0.8rem' }}>
                                              {item.hashtags}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Zoom>
                                </Box>
                              ))}
                            </Box>

                            {/* Pagination */}
                            {totalPages > 1 && (
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 3,
                                  mt: 4,
                                  borderRadius: 3,
                                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                  border: '1px solid #dee2e6'
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
                                  <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                                    Page {contentPage} of {totalPages} • Showing {startIndex + 1}-{Math.min(endIndex, totalFilteredCount)} of {totalFilteredCount} items
                                  </Typography>
                                  <Pagination
                                    count={totalPages}
                                    page={contentPage}
                                    onChange={(event, page) => {
                                      setContentPage(page);
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    color="primary"
                                    size="large"
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        '&.Mui-selected': {
                                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                                          color: 'white',
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #ee5a52 0%, #e04a42 100%)'
                                          }
                                        }
                                      }
                                    }}
                                  />
                                </Stack>
                              </Paper>
                            )}
                          </>
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              p: { xs: 4, md: 8 },
                              textAlign: 'center',
                              borderRadius: 4,
                              background: selectedPlatform === 'xhs' ?
                                'linear-gradient(135deg, rgba(255, 107, 107, 0.05) 0%, rgba(255, 107, 107, 0.02) 100%)' :
                                selectedPlatform === 'facebook' ?
                                  'linear-gradient(135deg, rgba(24, 119, 242, 0.05) 0%, rgba(24, 119, 242, 0.02) 100%)' :
                                  'linear-gradient(135deg, rgba(52, 168, 83, 0.05) 0%, rgba(52, 168, 83, 0.02) 100%)',
                              border: selectedPlatform === 'xhs' ?
                                '2px solid rgba(255, 107, 107, 0.1)' :
                                selectedPlatform === 'facebook' ?
                                  '2px solid rgba(24, 119, 242, 0.1)' :
                                  '2px solid rgba(52, 168, 83, 0.1)',
                              position: 'relative',
                              overflow: 'hidden',
                              minHeight: 300,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -50,
                                left: -50,
                                right: -50,
                                bottom: -50,
                                background: selectedPlatform === 'xhs' ?
                                  'radial-gradient(circle at 30% 30%, rgba(255, 107, 107, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255, 107, 107, 0.05) 0%, transparent 50%)' :
                                  selectedPlatform === 'facebook' ?
                                    'radial-gradient(circle at 30% 30%, rgba(24, 119, 242, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(24, 119, 242, 0.05) 0%, transparent 50%)' :
                                    'radial-gradient(circle at 30% 30%, rgba(52, 168, 83, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(52, 168, 83, 0.05) 0%, transparent 50%)',
                                animation: 'float 20s ease-in-out infinite',
                                zIndex: 0
                              }
                            }}
                          >
                            {/* Floating Orbs */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '20%',
                                right: '15%',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: selectedPlatform === 'xhs' ? 'rgba(255, 107, 107, 0.1)' :
                                  selectedPlatform === 'facebook' ? 'rgba(24, 119, 242, 0.1)' :
                                    'rgba(52, 168, 83, 0.1)',
                                animation: 'float 15s ease-in-out infinite reverse',
                                zIndex: 0
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: '25%',
                                left: '10%',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: selectedPlatform === 'xhs' ? 'rgba(255, 107, 107, 0.08)' :
                                  selectedPlatform === 'facebook' ? 'rgba(24, 119, 242, 0.08)' :
                                    'rgba(52, 168, 83, 0.08)',
                                animation: 'float 12s ease-in-out infinite',
                                zIndex: 0
                              }}
                            />

                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                              <Avatar
                                sx={{
                                  width: { xs: 80, md: 100 },
                                  height: { xs: 80, md: 100 },
                                  mx: 'auto',
                                  mb: 4,
                                  background: selectedPlatform === 'xhs' ?
                                    'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.05) 100%)' :
                                    selectedPlatform === 'facebook' ?
                                      'linear-gradient(135deg, rgba(24, 119, 242, 0.15) 0%, rgba(24, 119, 242, 0.05) 100%)' :
                                      'linear-gradient(135deg, rgba(52, 168, 83, 0.15) 0%, rgba(52, 168, 83, 0.05) 100%)',
                                  border: selectedPlatform === 'xhs' ? '3px solid rgba(255, 107, 107, 0.2)' :
                                    selectedPlatform === 'facebook' ? '3px solid rgba(24, 119, 242, 0.2)' :
                                      '3px solid rgba(52, 168, 83, 0.2)',
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: selectedPlatform === 'xhs' ?
                                    '0 8px 32px rgba(255, 107, 107, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' :
                                    selectedPlatform === 'facebook' ?
                                      '0 8px 32px rgba(24, 119, 242, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' :
                                      '0 8px 32px rgba(52, 168, 83, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                                  animation: 'float 6s ease-in-out infinite',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.1) rotate(5deg)',
                                    boxShadow: selectedPlatform === 'xhs' ?
                                      '0 12px 40px rgba(255, 107, 107, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)' :
                                      selectedPlatform === 'facebook' ?
                                        '0 12px 40px rgba(24, 119, 242, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)' :
                                        '0 12px 40px rgba(52, 168, 83, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                                  }
                                }}
                              >
                                {selectedPlatform === 'xhs' ? <LanguageIcon sx={{ fontSize: { xs: 40, md: 50 }, color: '#ff6b6b' }} /> :
                                  selectedPlatform === 'facebook' ? <FacebookIcon sx={{ fontSize: { xs: 40, md: 50 }, color: '#1877f2' }} /> :
                                    <GoogleIcon sx={{ fontSize: { xs: 40, md: 50 }, color: '#34a853' }} />}
                              </Avatar>

                              <Typography
                                variant="h4"
                                sx={{
                                  color: '#2c3e50',
                                  mb: 3,
                                  fontWeight: 700,
                                  fontSize: { xs: '1.5rem', md: '2rem' },
                                  textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                  background: selectedPlatform === 'xhs' ?
                                    'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)' :
                                    selectedPlatform === 'facebook' ?
                                      'linear-gradient(135deg, #1877f2 0%, #166fe5 100%)' :
                                      'linear-gradient(135deg, #34a853 0%, #137333 100%)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent'
                                }}
                              >
                                {contentFilter === 'inactive' ? t('aiGenerator.noInactiveItems') :
                                  contentFilter === 'active' ? t('aiGenerator.noActiveItems') :
                                    selectedPlatform === 'xhs' ? t('aiGenerator.noXHSPosts') :
                                      selectedPlatform === 'facebook' ? t('aiGenerator.noFacebookPosts') :
                                        t('aiGenerator.noGoogleReviews')}
                              </Typography>

                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 400,
                                  fontSize: { xs: '1rem', md: '1.1rem' },
                                  lineHeight: 1.6,
                                  maxWidth: 500,
                                  mx: 'auto',
                                  textShadow: '0 1px 5px rgba(0,0,0,0.05)'
                                }}
                              >
                                {contentFilter === 'inactive' ? t('aiGenerator.noInactiveFoundDesc') :
                                  contentFilter === 'active' ? t('aiGenerator.noActiveFoundDesc') :
                                    t('aiGenerator.noContentFoundDesc', { platform: selectedPlatform === 'xhs' ? t('aiGenerator.xiaohongshuPosts') : selectedPlatform === 'facebook' ? t('aiGenerator.facebookPosts') : t('aiGenerator.googleReviews') })}
                                <br />
                                {contentFilter !== 'inactive' && contentFilter !== 'active' && t('aiGenerator.generateHint')}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      })()}
                    </Box>
                  )}

                </Box>
              </Fade>
            )}

            {/* Image Detail Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              {selectedImage && (
                <>
                  <DialogTitle
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 4,
                      px: 4,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                      <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        width: 56,
                        height: 56,
                        border: '2px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}>
                        <ImageIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.5rem' }}>
                          {selectedImage.title || t('aiGenerator.xhsContentDetails') || 'XHS Content Details'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={handleCloseDialog}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.3)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
                    <Grid container>
                      <Grid item xs={12} md={7}>
                        <Box sx={{
                          p: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 500,
                          bgcolor: '#ffffff'
                        }}>
                          <Box
                            component="img"
                            src={selectedImage.image_url}
                            alt={`XHS Post - ${selectedImage.title || 'Untitled'}`}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '70vh',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: 3,
                              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.01)',
                                boxShadow: '0 16px 50px rgba(0,0,0,0.2)'
                              }
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 3,
                              flexDirection: 'column',
                              border: '2px dashed #dee2e6'
                            }}
                          >
                            <Avatar sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              mb: 3,
                              width: 80,
                              height: 80
                            }}>
                              <ImageIcon sx={{ fontSize: 40, color: '#667eea' }} />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                              {t('storeImages.noImages')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Box sx={{
                          p: 4,
                          minHeight: 500,
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: '#ffffff',
                          borderLeft: { md: '1px solid #e9ecef' }
                        }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, pb: 1.5, borderBottom: '3px solid #667eea' }}>
                            <Typography variant="h5" sx={{
                              color: '#2c3e50',
                              fontWeight: 700
                            }}>
                              {t('aiGenerator.contentDetails') || 'Content Details'}
                            </Typography>
                            {!isEditingDetails ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={handleEditDetails}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 600
                                }}
                              >
                                {t('aiGenerator.edit') || 'Edit'}
                              </Button>
                            ) : (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={savingDetails}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  {t('common.cancel') || 'Cancel'}
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleSaveDetails}
                                  disabled={savingDetails}
                                  startIcon={savingDetails ? <CircularProgress size={16} /> : null}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  }}
                                >
                                  {savingDetails ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                                </Button>
                              </Stack>
                            )}
                          </Stack>

                          <Stack spacing={3}>
                            {/* Title */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('aiGenerator.titleLabel') || 'Title'}
                              </Typography>
                              {isEditingDetails ? (
                                <TextField
                                  fullWidth
                                  value={editDetailsData.title}
                                  onChange={(e) => setEditDetailsData({ ...editDetailsData, title: e.target.value })}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              ) : (
                                selectedImage.title && (
                                  <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700, lineHeight: 1.4 }}>
                                    {selectedImage.title}
                                  </Typography>
                                )
                              )}
                            </Box>

                            {/* Content */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('aiGenerator.contentLabel') || 'Content'}
                              </Typography>
                              {isEditingDetails ? (
                                <TextField
                                  fullWidth
                                  value={editDetailsData.content}
                                  onChange={(e) => setEditDetailsData({ ...editDetailsData, content: e.target.value })}
                                  variant="outlined"
                                  multiline
                                  rows={8}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              ) : (
                                selectedImage.content && (
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#2c3e50',
                                      lineHeight: 1.8,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {selectedImage.content}
                                  </Typography>
                                )
                              )}
                            </Box>

                            {/* Hashtags */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('aiGenerator.hashtagsLabel') || 'Hashtags'}
                              </Typography>
                              {isEditingDetails ? (
                                <TextField
                                  fullWidth
                                  value={editDetailsData.hashtags}
                                  onChange={(e) => setEditDetailsData({ ...editDetailsData, hashtags: e.target.value })}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              ) : (
                                selectedImage.hashtags && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#667eea',
                                      fontWeight: 500,
                                      lineHeight: 1.6
                                    }}
                                  >
                                    {selectedImage.hashtags}
                                  </Typography>
                                )
                              )}
                            </Box>

                            {/* Active/Inactive Status */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('aiGenerator.status') || 'Status'}
                              </Typography>
                              {isEditingDetails ? (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={editDetailsData.is_active}
                                      onChange={(e) => setEditDetailsData({ ...editDetailsData, is_active: e.target.checked })}
                                      color="primary"
                                    />
                                  }
                                  label={editDetailsData.is_active ? t('aiGenerator.active') : t('aiGenerator.inactive')}
                                  sx={{
                                    '& .MuiFormControlLabel-label': {
                                      fontWeight: 600,
                                      color: editDetailsData.is_active ? '#27ae60' : '#e74c3c'
                                    }
                                  }}
                                />
                              ) : (
                                <StatusChip
                                  active={selectedImage.is_active !== false}
                                  loading={statusUpdatingId === selectedImage.id}
                                  onToggle={() => handleToggleStatus(selectedImage)}
                                />
                              )}
                            </Box>

                            <Box sx={{ pt: 2, borderTop: '1px solid #e9ecef' }}>
                              {/* Created Date */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                  {t('storeImages.createdDate')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {new Date(selectedImage.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                  {new Date(selectedImage.created_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>

                              {selectedImage.updated_at !== selectedImage.created_at && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                    {t('storeImages.lastUpdated')}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                    {new Date(selectedImage.updated_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                    {new Date(selectedImage.updated_at).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Image URL */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('storeImages.imageUrl')}
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: '#f8f9fa',
                                  borderRadius: 2,
                                  border: '1px solid #dee2e6',
                                  wordBreak: 'break-all',
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  color: '#495057',
                                  lineHeight: 1.4,
                                  maxHeight: 100,
                                  overflow: 'auto'
                                }}
                              >
                                {selectedImage.image_url}
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </>
              )}
            </Dialog>

            {/* Context Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                }}
                sx={{ color: '#667eea' }}
              >
                <AutoAwesomeIcon sx={{ mr: 1 }} />
                {t('aiGenerator.aiProcess')}
              </MenuItem>
            </Menu>

            {/* AI Content Generator Dialog */}
            <Dialog
              open={generatorOpen}
              onClose={handleCloseGenerator}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(102, 126, 234, 0.3)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 4,
                  px: 4,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', md: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 2.5, md: 3 },
                    position: 'relative',
                    zIndex: 1,
                    flexDirection: { xs: 'column', md: 'row' },
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      flexShrink: 0,
                      flexGrow: { xs: 0, md: 1 }
                    }}
                  >
                    <Avatar sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      width: 56,
                      height: 56,
                      border: '2px solid rgba(255,255,255,0.4)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }}>
                      <AutoAwesomeIcon sx={{ fontSize: 28, color: 'white' }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          mb: 0.5,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          lineHeight: 1.2,
                          textAlign: { xs: 'center', md: 'left' }
                        }}
                      >
                        {t('aiGenerator.aiContentGenerator')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          opacity: 0.95,
                          fontSize: { xs: '0.95rem', md: '1rem' },
                          lineHeight: 1.4,
                          maxWidth: { md: 360 },
                          mx: { xs: 'auto', md: 0 }
                        }}
                      >
                        {t('aiGenerator.generateEngagingContent', { platform: selectedPlatform === 'xhs' ? t('aiGenerator.xiaohongshu') : selectedPlatform === 'facebook' ? t('aiGenerator.facebook') : t('aiGenerator.googleMaps') })}
                      </Typography>
                    </Box>
                  </Stack>
                  {/* Quota Display */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: { xs: 2.5, md: 3 },
                      py: { xs: 1.5, md: 1.75 },
                      borderRadius: '50px',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                      position: 'relative',
                      zIndex: 1,
                      transition: 'all 0.3s ease',
                      alignSelf: { xs: 'center', md: 'auto' },
                      minWidth: { xs: '100%', md: 'auto' },
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      maxWidth: { md: 220 },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontWeight: 600,
                          fontSize: { xs: '0.8rem', md: '0.85rem' },
                          letterSpacing: '0.3px'
                        }}
                      >
                        Quota:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: quota.used_quota >= quota.quota_limit ? '#ffebee' : 'white',
                          fontWeight: 700,
                          fontSize: { xs: '0.95rem', md: '1rem' },
                          letterSpacing: '0.5px',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {quota.used_quota.toLocaleString()} / {quota.quota_limit.toLocaleString()}
                      </Typography>
                    </Box>
                    {quota.used_quota >= quota.quota_limit * 0.8 && (
                      <Chip
                        label={quota.used_quota >= quota.quota_limit ? 'Full' : 'Low'}
                        size="small"
                        sx={{
                          height: 24,
                          backgroundColor: quota.used_quota >= quota.quota_limit
                            ? 'rgba(244, 67, 54, 0.95)'
                            : 'rgba(255, 193, 7, 0.95)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      />
                    )}
                    {/* Progress indicator (hidden for cleaner desktop appearance) */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: 20,
                        right: 20,
                        height: '2px',
                        borderRadius: '50px',
                        background: `linear-gradient(90deg, 
                    ${quota.used_quota >= quota.quota_limit ? '#f44336' : quota.used_quota >= quota.quota_limit * 0.8 ? '#ffc107' : '#4caf50'} 
                    ${(quota.used_quota / quota.quota_limit) * 100}%, 
                    rgba(255, 255, 255, 0.15) ${(quota.used_quota / quota.quota_limit) * 100}%
                  )`,
                        opacity: { xs: 0, md: 0 },
                        display: { xs: 'none', md: 'block' },
                        transition: 'opacity 0.3s ease'
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseGenerator}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                  {!generatedContent ? (
                    <>
                      <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600, pt: 2 }}>
                        {t('aiGenerator.platformBusinessInfo')}
                      </Typography>

                      {/* History Button */}
                      {inputHistory.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <Button
                            startIcon={<HistoryIcon />}
                            onClick={(e) => setHistoryAnchorEl(e.currentTarget)}
                            variant="outlined"
                            size="small"
                            sx={{
                              color: '#667eea',
                              borderColor: '#667eea',
                              '&:hover': {
                                borderColor: '#5568d3',
                                bgcolor: 'rgba(102, 126, 234, 0.08)'
                              }
                            }}
                          >
                            {t('aiGenerator.loadFromHistory')} ({inputHistory.length})
                          </Button>
                        </Box>
                      )}

                      {/* History Menu */}
                      <Menu
                        anchorEl={historyAnchorEl}
                        open={historyMenuOpen}
                        onClose={() => setHistoryAnchorEl(null)}
                        PaperProps={{
                          sx: {
                            maxHeight: 400,
                            width: '400px',
                            mt: 1
                          }
                        }}
                      >
                        {inputHistory.length === 0 ? (
                          <MenuItem disabled>
                            <Typography variant="body2" sx={{ color: '#6c757d', textAlign: 'center', width: '100%' }}>
                              {t('aiGenerator.noHistoryAvailable')}
                            </Typography>
                          </MenuItem>
                        ) : (
                          inputHistory.slice().reverse().map((entry) => (
                            <MenuItem
                              key={entry.id}
                              onClick={() => loadHistoryEntry(entry)}
                              sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                  bgcolor: 'rgba(102, 126, 234, 0.08)'
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                    {entry.name || 'Untitled'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                                    {entry.location || 'No location'}
                                  </Typography>
                                  {entry.businessNature && (
                                    <Typography variant="caption" sx={{ color: '#667eea', display: 'block', mt: 0.5 }}>
                                      {entry.businessNature}
                                    </Typography>
                                  )}
                                  {entry.timestamp && (
                                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                                      {new Date(entry.timestamp).toLocaleDateString()} {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                  )}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => deleteHistoryEntry(entry.id, e)}
                                  sx={{
                                    color: '#e74c3c',
                                    '&:hover': {
                                      bgcolor: 'rgba(231, 76, 60, 0.1)'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </MenuItem>
                          ))
                        )}
                      </Menu>

                      <TextField
                        label={t('aiGenerator.businessNameLabel')}
                        value={businessInfo.name}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, name: e.target.value })}
                        fullWidth
                        variant="outlined"
                        required
                        helperText={t('aiGenerator.businessNameHelper')}
                      />

                      <TextField
                        label={t('aiGenerator.locationLabel')}
                        value={businessInfo.location}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, location: e.target.value })}
                        fullWidth
                        variant="outlined"
                        required
                        helperText={t('aiGenerator.locationHelper')}
                      />

                      <TextField
                        label={t('aiGenerator.sellingPointLabel')}
                        value={businessInfo.sellingPoint}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, sellingPoint: e.target.value })}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        helperText={t('aiGenerator.sellingPointHelper')}
                      />

                      <TextField
                        label={t('aiGenerator.businessNatureLabel')}
                        value={businessInfo.businessNature}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, businessNature: e.target.value })}
                        fullWidth
                        variant="outlined"
                        required
                        helperText={t('aiGenerator.businessNatureHelper')}
                      />


                      {selectedPlatform === 'xhs' && (
                        <TextField
                          label={t('aiGenerator.referencedHashtagsLabel')}
                          value={businessInfo.hashtags}
                          onChange={(e) => setBusinessInfo({ ...businessInfo, hashtags: e.target.value })}
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={2}
                          helperText={t('aiGenerator.referencedHashtagsHelper')}
                        />
                      )}

                      <Autocomplete
                        freeSolo
                        options={categories}
                        value={businessInfo.category || null}
                        onChange={(event, newValue) => {
                          setBusinessInfo({ ...businessInfo, category: newValue || '' });
                        }}
                        onInputChange={(event, newInputValue) => {
                          setBusinessInfo({ ...businessInfo, category: newInputValue });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            variant="outlined"
                            helperText="Select an existing category or type a new one"
                            placeholder="e.g., Food, Service, Product"
                          />
                        )}
                      />

                      {/* Batch Count Input - Only for XHS platform */}
                      {selectedPlatform === 'xhs' && (
                        <TextField
                          label={t('aiGenerator.numberOfRecordsLabel')}
                          type="number"
                          value={batchCount}
                          onChange={(e) => setBatchCount(e.target.value)}
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 1, max: 100 }}
                          helperText={t('aiGenerator.numberOfRecordsHelper')}
                        />
                      )}

                      {/* Image Selection for XHS */}
                      {selectedPlatform === 'xhs' && (
                        <Box>
                          <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                            {t('aiGenerator.selectImagesOptional')}
                            {batchCount > 1 && (
                              <Typography component="span" variant="body2" sx={{ ml: 1, color: '#667eea', fontWeight: 400 }}>
                                {t('aiGenerator.uploadMultipleForBatch')}
                              </Typography>
                            )}
                          </Typography>


                          <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                              <Box
                                sx={{
                                  flex: 1,
                                  border: '2px dashed #667eea',
                                  borderRadius: 2,
                                  p: 3,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: '#5568d3',
                                    backgroundColor: 'rgba(102, 126, 234, 0.05)'
                                  }
                                }}
                                onClick={() => {
                                  // Always allow multiple file selection
                                  document.getElementById('multiple-image-upload').click();
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 32, color: '#667eea', mb: 1 }} />
                                <Typography variant="subtitle1" sx={{ color: '#2c3e50', mb: 0.5 }}>
                                  {t('aiGenerator.uploadImages')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                  {t('aiGenerator.clickToSelectMultiple')}
                                </Typography>
                                <input
                                  id="image-upload"
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageSelect}
                                  style={{ display: 'none' }}
                                />
                                <input
                                  id="multiple-image-upload"
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleMultipleImageSelect}
                                  style={{ display: 'none' }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  flex: 1,
                                  border: '2px dashed #ff6b6b',
                                  borderRadius: 2,
                                  p: 3,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: '#ee5a52',
                                    backgroundColor: 'rgba(255, 107, 107, 0.05)'
                                  }
                                }}
                                onClick={handleOpenImageSelector}
                              >
                                <AutoAwesomeIcon sx={{ fontSize: 32, color: '#ff6b6b', mb: 1 }} />
                                <Typography variant="subtitle1" sx={{ color: '#2c3e50', mb: 0.5 }}>
                                  {t('aiGenerator.chooseFromXHSImages')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                  {t('aiGenerator.selectFromExistingImages')}
                                </Typography>
                              </Box>
                            </Stack>

                            {imagePreviews.length > 0 && (
                              <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>
                                  {t('aiGenerator.selectedImages', { count: imagePreviews.length })}
                                  {selectedXhsImages.length > 0 && (
                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: '#ff6b6b', fontWeight: 600 }}>
                                      {t('aiGenerator.fromXHSLibrary')}
                                    </Typography>
                                  )}
                                </Typography>
                                <Grid container spacing={2}>
                                  {imagePreviews.map((imgData, index) => (
                                    <Grid item xs={6} sm={4} md={3} key={index}>
                                      <Box sx={{ position: 'relative' }}>
                                        <Box
                                          component="img"
                                          src={imgData.preview}
                                          alt={`Selected ${index + 1}`}
                                          sx={{
                                            width: '100%',
                                            height: 120,
                                            objectFit: 'cover',
                                            borderRadius: 2,
                                            border: selectedXhsImages.length > 0 ? '2px solid #ff6b6b' : '2px solid #667eea'
                                          }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const previewUrl = imgData.preview;
                                            // Remove from previews
                                            const newPreviews = imagePreviews.filter((_, i) => i !== index);
                                            // Remove from uploaded images if it's an uploaded file
                                            const newImages = uploadedImages.filter((_, i) => i !== index);
                                            // Remove from selectedXhsImages by matching image_url
                                            const newXhsImages = selectedXhsImages.filter(img => img.image_url !== previewUrl);
                                            setImagePreviews(newPreviews);
                                            setUploadedImages(newImages);
                                            setSelectedXhsImages(newXhsImages);
                                          }}
                                          sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            color: 'white',
                                            '&:hover': {
                                              bgcolor: 'rgba(0,0,0,0.9)'
                                            }
                                          }}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                                <Button
                                  variant="outlined"
                                  onClick={clearImage}
                                  sx={{ mt: 2 }}
                                  startIcon={<CloseIcon />}
                                >
                                  {t('aiGenerator.clearAllImages')}
                                </Button>
                              </Box>
                            )}
                          </Stack>


                          {uploadingImage && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                              <CircularProgress size={24} sx={{ color: '#667eea' }} />
                              <Typography variant="body2" sx={{ color: '#6c757d', mt: 1 }}>
                                {t('aiGenerator.uploadingImage')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      <FormControl fullWidth>
                        <InputLabel id="language-select-label">{t('aiGenerator.languageLabel')}</InputLabel>
                        <Select
                          labelId="language-select-label"
                          value={businessInfo.language}
                          label={t('aiGenerator.languageLabel')}
                          disabled={true}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        >
                          <MenuItem value="Chinese">{t('aiGenerator.chinese')}</MenuItem>
                          <MenuItem value="English">{t('aiGenerator.english')}</MenuItem>
                          <MenuItem value="Malay">{t('aiGenerator.malay')}</MenuItem>
                        </Select>
                      </FormControl>

                    </>
                  ) : (
                    <>
                      {selectedPlatform === 'xhs' && generatedContent.title && (
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                              {t('aiGenerator.titleLabel')}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(generatedContent.title, 'title')}
                              sx={{
                                bgcolor: copied.title ? '#27ae60' : '#667eea',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: copied.title ? '#229954' : '#5568d3',
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {copied.title ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Stack>
                          <Typography variant="body1" sx={{ color: '#2c3e50', fontSize: '1.1rem', lineHeight: 1.6 }}>
                            {generatedContent.title}
                          </Typography>
                        </Paper>
                      )}

                      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                            {selectedPlatform === 'xhs' ? t('aiGenerator.content') : selectedPlatform === 'facebook' ? t('aiGenerator.facebookPost') : t('aiGenerator.googleMapsReview')}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(generatedContent.content || generatedContent, 'content')}
                            sx={{
                              bgcolor: copied.content ? '#27ae60' : '#667eea',
                              color: 'white',
                              '&:hover': {
                                bgcolor: copied.content ? '#229954' : '#5568d3',
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {copied.content ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                          </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ color: '#2c3e50', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                          {generatedContent.content || generatedContent}
                        </Typography>
                      </Paper>

                      {selectedPlatform === 'xhs' && generatedContent.hashtags && (
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                              {t('aiGenerator.hashtags')}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(generatedContent.hashtags, 'hashtags')}
                              sx={{
                                bgcolor: copied.hashtags ? '#27ae60' : '#667eea',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: copied.hashtags ? '#229954' : '#5568d3',
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {copied.hashtags ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Stack>
                          <Typography variant="body1" sx={{ color: '#667eea', fontSize: '1rem' }}>
                            {generatedContent.hashtags}
                          </Typography>
                        </Paper>
                      )}
                    </>
                  )}

                  {generating && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #667eea',
                        bgcolor: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                      }}
                    >
                      {batchProgress.isBatchGenerating ? (
                        // Batch generation progress
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <CircularProgress size={32} sx={{ color: '#667eea' }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                                {t('aiGenerator.batchGeneratingContent')}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                                {t('aiGenerator.generatingRecords', { total: batchProgress.total })}
                              </Typography>
                            </Box>
                          </Stack>

                          {/* Progress bar */}
                          <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                                {t('aiGenerator.progress', { current: batchProgress.current, total: batchProgress.total })}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                {Math.round((batchProgress.current / batchProgress.total) * 100)}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={(batchProgress.current / batchProgress.total) * 100}
                              sx={{
                                height: 10,
                                borderRadius: 5,
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#667eea',
                                  borderRadius: 5
                                }
                              }}
                            />
                          </Box>

                          {/* Status breakdown */}
                          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                                {t('aiGenerator.completed')}
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#27ae60', fontWeight: 700 }}>
                                {batchProgress.completed}
                              </Typography>
                            </Box>
                            {batchProgress.failed > 0 && (
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                                  {t('aiGenerator.failed')}
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#e74c3c', fontWeight: 700 }}>
                                  {batchProgress.failed}
                                </Typography>
                              </Box>
                            )}
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>
                                {t('aiGenerator.remaining')}
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                                {batchProgress.total - batchProgress.current}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography variant="body2" sx={{ color: '#6c757d', textAlign: 'center', mt: 1 }}>
                            {t('aiGenerator.pleaseWaitGeneratingXHS')}
                          </Typography>
                        </Stack>
                      ) : (
                        // Single generation loading
                        <>
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <CircularProgress size={24} sx={{ color: '#667eea' }} />
                            <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                              {t('aiGenerator.generatingContentWithAIXHS')}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#667eea'
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#6c757d', mt: 1, textAlign: 'center' }}>
                            {t('aiGenerator.pleaseWaitCreating')}
                          </Typography>
                        </>
                      )}
                    </Paper>
                  )}
                </Stack>
              </DialogContent>

              <DialogActions sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} />
                  {!generatedContent ? (
                    <Button
                      onClick={handleGenerateContent}
                      variant="contained"
                      disabled={generating || !businessInfo.name.trim() || !businessInfo.location.trim() || !businessInfo.businessNature.trim() || (selectedPlatform === 'xhs' && !businessInfo.hashtags.trim()) || platformTotalCount >= 1500}
                      startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8c 100%)',
                          transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                          background: 'rgba(0,0,0,0.12)',
                          color: 'rgba(0,0,0,0.26)'
                        },
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        color: 'white !important',
                        textTransform: 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {generating ? (batchProgress.isBatchGenerating
                        ? `${t('aiGenerator.generating')} (${batchProgress.current}/${batchProgress.total})`
                        : t('aiGenerator.generating')) :
                        (!businessInfo.name.trim() || !businessInfo.location.trim() || !businessInfo.businessNature.trim() || (selectedPlatform === 'xhs' && !businessInfo.hashtags.trim())) ?
                          t('aiGenerator.fillRequiredFields') : t('aiGenerator.generateContent')}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleGenerateContent}
                      variant="contained"
                      disabled={generating}
                      startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8c 100%)',
                          transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                          background: 'rgba(0,0,0,0.12)',
                          color: 'rgba(0,0,0,0.26)'
                        },
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        color: 'white !important',
                        textTransform: 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {generating ? (batchProgress.isBatchGenerating
                        ? `${t('aiGenerator.generating')} (${batchProgress.current}/${batchProgress.total})`
                        : t('aiGenerator.generating')) : t('aiGenerator.generateNewContent')}
                    </Button>
                  )}
                </Stack>
              </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialog.open} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
              <DialogTitle>{t('aiGenerator.editContent', { platform: selectedPlatform === 'xhs' ? 'XHS' : selectedPlatform === 'facebook' ? 'Facebook' : 'Google' })}</DialogTitle>
              <DialogContent sx={{ p: 3 }}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {/* Image Preview & Controls - Only for XHS */}
                  {selectedPlatform === 'xhs' && (
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: editPreviewSource ? '2px solid #e9ecef' : '2px dashed rgba(102, 126, 234, 0.4)',
                          bgcolor: '#f8f9fa',
                          minHeight: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                      >
                        {editPreviewSource ? (
                          <Box
                            component="img"
                            src={editPreviewSource}
                            alt="Edit Preview"
                            sx={{
                              width: '100%',
                              maxHeight: 320,
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <Stack spacing={1} alignItems="center" sx={{ py: 6, color: '#95a5a6' }}>
                            <ImageIcon sx={{ fontSize: 48, opacity: 0.6 }} />
                            <Typography variant="body2">
                              {t('aiGenerator.noImageSelected') || 'No image selected'}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={uploadingImage ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                          disabled={uploadingImage}
                          sx={{ flexGrow: 0 }}
                        >
                          {t('aiGenerator.uploadImage') || 'Upload Image'}
                          <input type="file" hidden accept="image/*" onChange={handleEditImageUpload} />
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CollectionsIcon />}
                          onClick={() => handleOpenImageSelector('edit')}
                          disabled={uploadingImage}
                        >
                          {t('aiGenerator.chooseFromLibrary') || 'Choose from Library'}
                        </Button>
                        {editPreviewSource && (
                          <Button
                            variant="text"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={clearEditImage}
                            disabled={uploadingImage}
                          >
                            {t('aiGenerator.removeImage') || 'Remove Image'}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  )}
                  {selectedPlatform === 'xhs' && (
                    <TextField label={t('aiGenerator.titleLabel')} value={editDialog.title} onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })} fullWidth />
                  )}
                  <TextField label={t('aiGenerator.content')} value={editDialog.content} onChange={(e) => setEditDialog({ ...editDialog, content: e.target.value })} fullWidth multiline rows={6} />
                  {selectedPlatform === 'xhs' && (
                    <TextField label={t('aiGenerator.hashtags')} value={editDialog.hashtags} onChange={(e) => setEditDialog({ ...editDialog, hashtags: e.target.value })} fullWidth />
                  )}
                  <Autocomplete
                    freeSolo
                    options={categories}
                    value={editDialog.category || null}
                    onChange={(event, newValue) => {
                      setEditDialog({ ...editDialog, category: newValue || '' });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setEditDialog({ ...editDialog, category: newInputValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        variant="outlined"
                        helperText="Select an existing category or type a new one"
                        placeholder="e.g., Food, Service, Product"
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editDialog.is_active}
                        onChange={(e) => setEditDialog({ ...editDialog, is_active: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={editDialog.is_active ? t('aiGenerator.active') : t('aiGenerator.inactive')}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 600,
                        color: editDialog.is_active ? '#27ae60' : '#e74c3c'
                      }
                    }}
                  />
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleCloseEditDialog}>{t('common.cancel')}</Button>
                <Button variant="contained" disabled={uploadingImage} onClick={async () => {
                  try {
                    const id = editDialog.id;
                    const brand = getCurrentBrand();
                    let endpoint;
                    let payload;
                    let imageUrlForPayload = editDialog.image_url || '';

                    if (selectedPlatform === 'xhs') {
                      if (editImageFile) {
                        const uploadedUrl = await uploadImageFileToLibrary(editImageFile);
                        if (!uploadedUrl) {
                          throw new Error(t('aiGenerator.failedToUploadImage') || 'Failed to upload image');
                        }
                        imageUrlForPayload = uploadedUrl;
                        setEditDialog(prev => ({ ...prev, image_url: uploadedUrl }));
                      } else if (!editPreviewSource) {
                        imageUrlForPayload = '';
                      } else if (editSelectedLibraryImage) {
                        imageUrlForPayload = editSelectedLibraryImage.image_url;
                      }
                    }

                    if (selectedPlatform === 'facebook') {
                      endpoint = `${API_ENDPOINTS.FACEBOOK_CONTENT}/${id}?brand=${brand}`;
                      payload = { content: editDialog.content, category: editDialog.category, image_url: imageUrlForPayload, is_active: editDialog.is_active };
                    } else if (selectedPlatform === 'google') {
                      endpoint = `${API_ENDPOINTS.GOOGLE_CONTENT}/${id}?brand=${brand}`;
                      payload = { content: editDialog.content, category: editDialog.category, image_url: imageUrlForPayload, is_active: editDialog.is_active };
                    } else {
                      endpoint = `${API_ENDPOINTS.XHS_CONTENT}/${id}?brand=${brand}`;
                      payload = { title: editDialog.title, content: editDialog.content, hashtags: editDialog.hashtags, image_url: imageUrlForPayload, category: editDialog.category, is_active: editDialog.is_active };
                    }
                    const resp = await fetch(endpoint, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                    if (!resp.ok) throw new Error('Failed to update');
                    handleCloseEditDialog();
                    fetchPlatformContent(selectedPlatform);
                    fetchCategories(); // Refresh categories in case a new one was added
                  } catch (e) {
                    setError(e.message);
                  }
                }}>{t('common.save')}</Button>
              </DialogActions>
            </Dialog>

            {/* XHS Image Selector Dialog */}
            <Dialog
              open={imageSelectorOpen}
              onClose={handleCloseImageSelector}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(255, 107, 107, 0.3)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 3,
                  px: 4
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.3)',
                    width: 40,
                    height: 40
                  }}>
                    <ImageIcon sx={{ fontSize: 20, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('aiGenerator.selectXHSImages')}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {selectedXhsImages.length > 0
                        ? `${selectedXhsImages.length} ${t('aiGenerator.imageSelected')}`
                        : t('aiGenerator.chooseMultipleImages')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseImageSelector}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 3 }}>
                {loadingXhsImages ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#ff6b6b', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#6c757d' }}>
                      {t('aiGenerator.loadingXHSImages')}
                    </Typography>
                  </Box>
                ) : xhsImages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ImageIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#6c757d', mb: 1 }}>
                      {t('aiGenerator.noXHSImagesAvailable')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      {t('aiGenerator.uploadToXHSLibraryFirst')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    width: '100%'
                  }}>
                    {xhsImages.map((image) => (
                      <XHSImageCard
                        key={image.id}
                        image={image}
                        isSelected={selectedXhsImages.some(img => img.id === image.id)}
                        onSelect={stableHandleXhsImageSelect}
                      />
                    ))}
                  </Box>
                )}

                {/* Pagination Controls */}
                {!loadingXhsImages && xhsImages.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
                    <Button
                      variant="outlined"
                      disabled={xhsImagePage === 1}
                      onClick={() => fetchXhsImages(xhsImagePage - 1)}
                      sx={{
                        borderColor: '#ff6b6b',
                        color: '#ff6b6b',
                        '&:hover': { borderColor: '#ee5a52', bgcolor: 'rgba(255,107,107,0.1)' }
                      }}
                    >
                      ← Previous
                    </Button>
                    <Typography sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 600,
                      color: '#333'
                    }}>
                      Page {xhsImagePage} of {xhsImageTotalPages || 1}
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={xhsImagePage >= xhsImageTotalPages}
                      onClick={() => fetchXhsImages(xhsImagePage + 1)}
                      sx={{
                        borderColor: '#ff6b6b',
                        color: '#ff6b6b',
                        '&:hover': { borderColor: '#ee5a52', bgcolor: 'rgba(255,107,107,0.1)' }
                      }}
                    >
                      Next →
                    </Button>
                    <Box sx={{ minWidth: 100 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={xhsItemsPerPage}
                          onChange={handleXhsItemsPerPageChange}
                          sx={{ borderRadius: 2, height: 36.5 }}
                        >
                          {[10, 20, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Button
                  onClick={handleCloseImageSelector}
                  sx={{ color: '#6c757d' }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  disabled={selectedXhsImages.length === 0}
                  onClick={handleConfirmXhsImageSelection}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ee5a52 0%, #e04a42 100%)'
                    },
                    '&:disabled': {
                      background: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)'
                    }
                  }}
                >
                  {selectedXhsImages.length > 0
                    ? t('aiGenerator.confirmSelection', { count: selectedXhsImages.length })
                    : t('aiGenerator.selectImages')}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Paper>
      </Container >
    </>
  );
};

export default XHSContentGenerator;
